import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { Maximize2, MapPin, ChevronDown, Bell, Clock3, Send, X, Film, School, FileDown, ChevronLeft, ChevronRight, Eye, User, Phone, CreditCard, MapPinned, Copy } from "lucide-react";
import { useLocation, useOutletContext } from "react-router-dom";
import { Slider } from "@mui/material";
import TrackingPageLite2 from "src/pages/tracking/TrackingPageLite2";
import useTripReplay from "src/hooks/useTripReplay";
import { useDevices } from "src/newComponents/dashboard/useDevices";
import { usePickupPoints } from "src/newComponents/dashboard/usePickupPoints";
import { useTravelHistory } from "src/newComponents/dashboard/useTravelHistory";
import { getLocalStorage } from "src/helpers/localStorage";
import getLoginId from "src/packages/common/utils/getLoginId";
import GoogleMapContainer from "src/packages/common/hoc/withGoogleMaps/MapContainer/MapContainer";
import getCustomMarker from "src/modules/Maps/Map/CustomMarker/CustomMarker";
import iconStartFlag from "src/modules/static/png/start.png";
import iconEndFlag from "src/modules/static/png/stop.png";

import {
  FiChevronDown,
  FiGrid,
  FiMapPin,
  FiMenu,
  FiMinus,
  FiMoreHorizontal,
  FiPlus,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import { FaSatellite, FaEllipsisV } from "react-icons/fa";
import ReplayControlPanel from "src/modules/TrackingControls/components/ReplayControlPanel";
import VehicleList from "src/modules/TrackingControls/components/vehicleList";
import { useTranslation } from "..";


const DEFAULT_VISIBLE_SECTIONS = {
  topStats: false,
  mapAlerts: false,
  operations: false,
  fleet: false,
  kpis: true,
  liveMaps: true,
  incidentDeviation: true,
  speedZones: true,
  trafficFlow: true,
};

const EVENT_CENTER_DEVICE_PLACEHOLDER = "please Select Device";
const EVENT_CENTER_EVENT_PLACEHOLDER = "Select";


const EVENT_CENTER_QUICK_RANGES = ["Today", "Yesterday", "This week", "Last Week", "This Month", "Last Month"];
const EVENT_CENTER_WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const GET_DATA_EVENT_CENTER = gql`
  query GetDataEventCenter(
    $id: Int
    $clientId: Int
    $isAst: Boolean
    $uniqueId: String
    $vehicleNumber: String
    $from: String
    $to: String
    $tripId: Int
    $driverName: String
    $contactNumber: String
    $fileName: String
    $plusCode: String
    $alertValue: [String!]
    $alertType: [String!]
  ) {
    getDataEventCenter(
      id: $id
      clientId: $clientId
      isAst: $isAst
      uniqueId: $uniqueId
      vehicleNumber: $vehicleNumber
      from: $from
      to: $to
      tripId: $tripId
      driverName: $driverName
      contactNumber: $contactNumber
      fileName: $fileName
      plusCode: $plusCode
      alertValue: $alertValue
      alertType: $alertType
    ) {
      clientId
      uniqueId
      vehicleNumber
      alertType
      alertValue
      fromTs
      toTs
      serverTs
      lat
      lng
      toLat
      toLng
      tripId
      alertCompleted
      viewStatus
      plusCode
      scheduledStartLoc
      scheduledEndLoc
      driverName
      contactNumber
      alertLevel
      fatigueLevel
      fileName
      attachements
      numberOfAttachment
      speed
      imageLinks
      videoLinks
      zipDownload
    }
  }
`;

const GET_ALERT_TYPE_CAMERA = gql`
  query GetAlertTypeCamera {
    getAlertTypeCamera {
      id
      name
      alertValue
    }
  }
`;

const GET_REPLAY_DATA_CAMERA = gql`
  query GetReplayDataCamera($uniqueid: String!, $alert_id: String!) {
    getReplayDataCamera(uniqueid: $uniqueid, alert_id: $alert_id) {
      distanceTravelledKms
      point {
        ts
        lat
        lng
        speed
        isHA
        isHB
        address
      }
    }
  }
`;

function createDefaultEventCenterRange() {
  const now = new Date();
  return {
    startDate: startOfDay(now),
    endDate: endOfDay(now),
  };
}

function createAutoOpenEventCenterRange() {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - 90);

  return {
    startDate: startOfDay(startDate),
    endDate: endOfDay(now),
  };
}

function normalizeEventCenterFilters(filters, options = {}) {
  const {
    forceIsAst = false,
    defaultAlertValues = [],
    omitClientIdWhenDeviceSelected = false,
  } = options;
  const hasSelectedDevice =
    filters.selectedDevice && filters.selectedDevice !== EVENT_CENTER_DEVICE_PLACEHOLDER;
  const normalizedSelectedAlertValues =
    filters.selectedEvents.length > 0
      ? filters.selectedEvents
        .map((event) => event?.alertValue)
        .filter((alertValue) => typeof alertValue === "string" && alertValue.trim().length > 0)
      : [];

  const resolvedAlertValues =
    normalizedSelectedAlertValues.length > 0
      ? normalizedSelectedAlertValues
      : (Array.isArray(defaultAlertValues) ? defaultAlertValues : []).filter(
        (alertValue) => typeof alertValue === "string" && alertValue.trim().length > 0
      );

  return {
    clientId:
      omitClientIdWhenDeviceSelected && hasSelectedDevice
        ? undefined
        : getLoginId(),
    isAst: forceIsAst ? true : undefined,
    uniqueId: hasSelectedDevice ? filters.selectedDevice : undefined,
    from: formatEventCenterDateTime(filters.startDate),
    to: formatEventCenterDateTime(filters.endDate),
    alertValue: resolvedAlertValues.length > 0 ? resolvedAlertValues : undefined,
  };
}

function normalizeEventCenterDeviceToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^it_/i, "");
}

function shouldAutoOpenEventCenterModal(rawValue) {
  if (rawValue == null) return false;
  const normalizedValue = String(rawValue).trim().toLowerCase();
  if (!normalizedValue) return true;
  return !["0", "false", "no", "off"].includes(normalizedValue);
}

function parseEventCenterDateValue(value) {
  if (!value) return Number.NaN;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? Number.NaN : parsed;
}

function getLatestEventCenterRow(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return rows.reduce((latestRow, currentRow) => {
    if (!latestRow) return currentRow;

    const latestTimestamp = parseEventCenterDateValue(
      latestRow?.raw?.serverTs || latestRow?.raw?.toTs || latestRow?.raw?.fromTs || latestRow?.time
    );
    const currentTimestamp = parseEventCenterDateValue(
      currentRow?.raw?.serverTs || currentRow?.raw?.toTs || currentRow?.raw?.fromTs || currentRow?.time
    );

    if (Number.isNaN(currentTimestamp)) return latestRow;
    if (Number.isNaN(latestTimestamp)) return currentRow;
    return currentTimestamp > latestTimestamp ? currentRow : latestRow;
  }, null);
}

function getEventCenterAttachmentCount(item) {
  const numericCount = Number(item?.numberOfAttachment);
  if (!Number.isNaN(numericCount) && numericCount > 0) return numericCount;
  return (item?.imageLinks?.length || 0) + (item?.videoLinks?.length || 0);
}

function normalizeEventCenterLinks(links) {
  if (Array.isArray(links)) {
    return links.filter((link) => typeof link === "string" && link.trim().length > 0);
  }

  if (typeof links === "string") {
    const trimmedLinks = links.trim();
    if (!trimmedLinks) return [];

    try {
      const parsedLinks = JSON.parse(trimmedLinks);
      if (Array.isArray(parsedLinks)) {
        return parsedLinks.filter((link) => typeof link === "string" && link.trim().length > 0);
      }
    } catch (error) {
      return [trimmedLinks];
    }

    return [trimmedLinks];
  }

  return [];
}

function parseEventCenterAttachments(attachments) {
  if (Array.isArray(attachments)) {
    return attachments
      .map((attachment) => {
        if (typeof attachment === "string") {
          return { fileName: attachment };
        }

        if (attachment && typeof attachment === "object" && typeof attachment.fileName === "string") {
          return { fileName: attachment.fileName };
        }

        return null;
      })
      .filter((attachment) => typeof attachment?.fileName === "string" && attachment.fileName.trim().length > 0);
  }

  if (typeof attachments === "string") {
    const trimmedAttachments = attachments.trim();
    if (!trimmedAttachments) return [];

    try {
      const parsedAttachments = JSON.parse(trimmedAttachments);
      return parseEventCenterAttachments(parsedAttachments);
    } catch (error) {
      return [];
    }
  }

  return [];
}

function getEventCenterFileNameFromPath(value) {
  if (typeof value !== "string" || !value.trim()) return "";

  const normalizedValue = value.trim().split("?")[0];
  const segments = normalizedValue.split("/");
  return decodeURIComponent(segments[segments.length - 1] || "").trim();
}

function extractEventCenterAlertId(value) {
  const fileName = getEventCenterFileNameFromPath(value);
  if (!fileName) return "";

  const nameWithoutExtension = fileName.replace(/\.[^.]+$/, "");
  const parts = nameWithoutExtension.split("_").filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}

function getEventCenterReplayAlertId(item) {
  const attachmentFileNames = parseEventCenterAttachments(item?.attachements).map((attachment) => attachment?.fileName);
  const linkFileNames = [...normalizeEventCenterLinks(item?.imageLinks), ...normalizeEventCenterLinks(item?.videoLinks)];
  const candidates = [item?.fileName, ...attachmentFileNames, ...linkFileNames];

  for (const candidate of candidates) {
    const alertId = extractEventCenterAlertId(candidate);
    if (alertId) {
      return alertId;
    }
  }

  return "";
}

function formatEventCenterReplayTimestamp(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return `${formatEventCenterDate(date)} ${formatEventCenterTime(date)}`;
}

function getEventCenterReplayDirection(point) {
  if (point?.isHA) return "HA";
  if (point?.isHB) return "HB";
  return "-";
}

function getEventCenterReplayViewportConfig() {
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;

  if (viewportWidth < 640) {
    return {
      maxZoom: 14,
      padding: {
        top: 32,
        right: 28,
        bottom: 110,
        left: 28,
      },
      flagSize: 22,
    };
  }

  if (viewportWidth < 1024) {
    return {
      maxZoom: 15,
      padding: {
        top: 36,
        right: 36,
        bottom: 120,
        left: 36,
      },
      flagSize: 24,
    };
  }

  return {
    maxZoom: viewportHeight < 760 ? 15 : 16,
    padding: {
      top: 40,
      right: 44,
      bottom: 128,
      left: 44,
    },
    flagSize: 26,
  };
}

function EventCenterReplayGoogleMap({ google, points, activeIndex, vehicleType, uniqueId, replayInterval }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const fullPathRef = useRef(null);
  const progressPathRef = useRef(null);
  const markerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const latLngsRef = useRef([]);
  const previousIndexRef = useRef(0);

  useEffect(() => {
    if (!google || !mapRef.current || !Array.isArray(points) || points.length === 0) {
      return undefined;
    }

    const validPoints = points.filter(
      (point) => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng))
    );

    if (validPoints.length === 0) {
      return undefined;
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
    }

    const map = mapInstanceRef.current;
    const viewportConfig = getEventCenterReplayViewportConfig();
    const latLngs = validPoints.map(
      (point) =>
        new google.maps.LatLng({
          lat: Number(point.lat),
          lng: Number(point.lng),
        })
    );
    latLngsRef.current = latLngs;

    if (fullPathRef.current) {
      fullPathRef.current.setMap(null);
    }
    if (progressPathRef.current) {
      progressPathRef.current.setMap(null);
    }
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    if (startMarkerRef.current) {
      startMarkerRef.current.setMap(null);
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.setMap(null);
    }
    if (markerRef.current?.setMap) {
      markerRef.current.setMap(null);
    } else if (markerRef.current?.remove) {
      markerRef.current.remove();
    }

    fullPathRef.current = new google.maps.Polyline({
      path: latLngs,
      geodesic: true,
      strokeColor: "#94a3b8",
      strokeOpacity: 0.9,
      strokeWeight: 4,
      map,
    });

    progressPathRef.current = new google.maps.Polyline({
      path: latLngs.slice(0, 1),
      geodesic: true,
      strokeColor: "#10b981",
      strokeOpacity: 1,
      strokeWeight: 5,
      map,
    });

    startMarkerRef.current = new google.maps.Marker({
      position: latLngs[0],
      map,
      icon: {
        url: iconStartFlag,
        scaledSize: new google.maps.Size(viewportConfig.flagSize, viewportConfig.flagSize),
      },
    });

    endMarkerRef.current = new google.maps.Marker({
      position: latLngs[latLngs.length - 1],
      map,
      icon: {
        url: iconEndFlag,
        scaledSize: new google.maps.Size(viewportConfig.flagSize, viewportConfig.flagSize),
      },
    });

    const CustomMarker = getCustomMarker(google);
    markerRef.current = new CustomMarker(
      {
        uniqueId: uniqueId || "event-center-replay",
        vehicleType: vehicleType || "Car",
        latitude: Number(validPoints[0].lat),
        longitude: Number(validPoints[0].lng),
        isOffline: false,
        isNoGps: false,
        haltStatus: false,
        idlingStatus: false,
        speed: Number(validPoints[0]?.speed) || 0,
        timestamp: validPoints[0]?.ts || Date.now(),
      },
      map,
      {
        closePopup: () => { },
        isOpenFor: () => false,
        openPopup: () => { },
        setPopupData: () => { },
      },
      () => { },
      null,
      () => ({}),
      { disablePopup: true }
    );
    markerRef.current.drawLine = false;
    previousIndexRef.current = 0;

    const bounds = new google.maps.LatLngBounds();
    latLngs.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, viewportConfig.padding);
    google.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > viewportConfig.maxZoom) {
        map.setZoom(viewportConfig.maxZoom);
      }
    });

    return () => {
      if (fullPathRef.current) fullPathRef.current.setMap(null);
      if (progressPathRef.current) progressPathRef.current.setMap(null);
      if (markerRef.current?.setMap) markerRef.current.setMap(null);
      if (markerRef.current?.remove) markerRef.current.remove();
      if (startMarkerRef.current) startMarkerRef.current.setMap(null);
      if (endMarkerRef.current) endMarkerRef.current.setMap(null);
    };
  }, [google, points, uniqueId, vehicleType]);

  useEffect(() => {
    const latLngs = latLngsRef.current;
    if (!latLngs.length) {
      return;
    }

    const safeIndex = Math.min(Math.max(activeIndex, 0), latLngs.length - 1);
    if (progressPathRef.current) {
      progressPathRef.current.setPath(latLngs.slice(0, safeIndex + 1));
    }
    if (markerRef.current) {
      const currentPoint = points?.[safeIndex];
      const didAdvanceOneStep = safeIndex === previousIndexRef.current + 1;

      if (didAdvanceOneStep && typeof markerRef.current.updateMarker === "function") {
        markerRef.current.updateMarker(
          {
            lat: parseFloat(Number(currentPoint?.lat || 0).toFixed(6)),
            lng: parseFloat(Number(currentPoint?.lng || 0).toFixed(6)),
          },
          {
            status: "running",
            mode: "replay",
            timestamp: currentPoint?.ts,
            speed: currentPoint?.speed,
          },
          replayInterval
        );
      } else if (typeof markerRef.current.setPosition === "function") {
        markerRef.current.setPosition(latLngs[safeIndex]);
      }
    }
    previousIndexRef.current = safeIndex;
  }, [activeIndex, points, replayInterval]);

  if (!Array.isArray(points) || points.length === 0) {
    return (
      <div className="grid h-[420px] place-items-center border-b border-[#d7dee8] bg-[#f8fafc] text-[14px] text-[#7b8797]">
        No Data
      </div>
    );
  }

  return <div ref={mapRef} className="h-[420px] overflow-hidden border-b border-[#d7dee8]" />;
}

function mapEventCenterRow(item, index) {
  return {
    id: item?.id ?? index + 1,
    raw: item,
    attachments: getEventCenterAttachmentCount(item),
    eventName: item?.alertType || item?.alertValue || "-",
    plateNumber: item?.vehicleNumber || "-",
    driver: item?.driverName || "-",
    belongTo: item?.clientId ? String(item.clientId) : "-",
    speed: item?.speed != null ? `${item.speed} km/h` : "-",
    time: item?.serverTs || item?.toTs || item?.fromTs || "-",
    address: item?.plusCode || item?.scheduledStartLoc || item?.scheduledEndLoc || "-",
  };
}

function formatEventCenterDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatEventCenterTime(date) {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

function formatEventCenterDateTime(date) {
  return `${formatEventCenterDate(date)} ${formatEventCenterTime(date)}`;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}

function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
}

function startOfWeek(date) {
  const result = startOfDay(date);
  result.setDate(result.getDate() - result.getDay());
  return result;
}

function endOfWeek(date) {
  const result = endOfDay(date);
  result.setDate(result.getDate() + (6 - result.getDay()));
  return result;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function isSameDay(firstDate, secondDate) {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
}

function buildMonthGrid(monthDate) {
  const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const firstVisibleDay = new Date(firstDayOfMonth);
  firstVisibleDay.setDate(firstVisibleDay.getDate() - firstVisibleDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = new Date(firstVisibleDay);
    cellDate.setDate(firstVisibleDay.getDate() + index);
    return cellDate;
  });
}

function EventCenterSelect({ label, value, options, isOpen, onToggle, onSelect, className = "" }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const isSelected = value && value !== EVENT_CENTER_DEVICE_PLACEHOLDER && value !== EVENT_CENTER_EVENT_PLACEHOLDER;
  const getOptionValue = (option) => (typeof option === "string" ? option : option?.uniqueId);
  const getOptionKey = (option) => getOptionValue(option) || option?.vehicleNumber || option;
  const getOptionLabel = (option) => {
    if (option === EVENT_CENTER_DEVICE_PLACEHOLDER) {
      return t("eventCenter.select_device_placeholder");
    }
    if (typeof option === "string") {
      return option;
    }
    return option?.vehicleNumber || option?.uniqueId || "-";
  };
  const selectedOption = options.find((option) => getOptionValue(option) === value);
  const displayValue = isSelected ? getOptionLabel(selectedOption) : getOptionLabel(EVENT_CENTER_DEVICE_PLACEHOLDER);
  const filteredOptions = options.filter((option) => {
    if (option === EVENT_CENTER_DEVICE_PLACEHOLDER) return true;
    return getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className={`relative w-full min-w-[220px] max-w-[360px] ${className}`}>
      <div className="flex items-center gap-3">
        <span className="w-[72px] shrink-0 text-[14px] text-[#555]">{label}</span>
        <button
          type="button"
          onClick={onToggle}
          className={`flex h-[36px] min-w-0 flex-1 items-center justify-between rounded bg-white px-3 text-left text-[14px] text-[#6b7280] ${isSelected
            ? "border border-[#10b981] shadow-[0_0_0_1px_rgba(16,185,129,0.08)]"
            : "border border-[#cfd6df]"
            }`}
        >
          <span className="truncate">{displayValue}</span>
          <div className="ml-2 flex items-center gap-2">
            {isSelected ? (
              <>
                <span className="h-4 w-px bg-[#d7dee8]" />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(EVENT_CENTER_DEVICE_PLACEHOLDER);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      onSelect(EVENT_CENTER_DEVICE_PLACEHOLDER);
                    }
                  }}
                  className="pt-[1px] text-[#98a2b3]"
                >
                  ×
                </span>
              </>
            ) : null}
            <ChevronDown className={`h-4 w-4 text-[#b3bac5] transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </div>
        </button>
      </div>
      {isOpen ? (
        <div className="absolute left-[72px] top-full z-50 mt-2 w-[calc(100%-72px)] min-w-[220px] overflow-hidden rounded border border-[#dbe4ee] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
          <div className="border-b border-[#edf1f5] p-3">
            <div className="flex h-[36px] items-center gap-2 rounded border border-[#d7dee8] bg-[#f8fafc] px-3">
              <FiSearch className="h-4 w-4 text-[#98a2b3]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Rechercher un appareil"
                className="h-full w-full border-none bg-transparent text-[14px] text-[#495568] outline-none placeholder:text-[#98a2b3]"
              />
            </div>
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-[14px] text-[#98a2b3]">Aucun appareil</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={getOptionKey(option)}
                  type="button"
                  onClick={() => onSelect(getOptionValue(option))}
                  className="block w-full px-4 py-3 text-left text-[14px] text-[#666] hover:bg-[#f5f9ff]"
                >
                  {getOptionLabel(option)}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EventCenterMultiSelect({
  label,
  values,
  options,
  isOpen,
  onToggle,
  onChange,
  className = "",
  isLoading = false,
}) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const displayValues = values.length > 0 ? values : [EVENT_CENTER_EVENT_PLACEHOLDER];
  const hasSelection = values.length > 0;
  const getOptionKey = (option) =>
    typeof option === "string" ? option : option?.id ?? option?.alertValue ?? option?.name;
  const getOptionLabel = (option) => {
    if (option === EVENT_CENTER_EVENT_PLACEHOLDER) {
      return t("eventCenter.select_placeholder");
    }
    return typeof option === "string" ? option : option?.name || option?.alertValue || "-";
  };
  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`relative w-full min-w-[260px] max-w-[500px] ${className}`}>
      <div className="flex items-start gap-3">
        <span className="w-[90px] shrink-0 pt-[7px] text-[14px] text-[#555]">{label}</span>
        <button
          type="button"
          onClick={onToggle}
          className={`flex min-h-[36px] min-w-0 flex-1 items-start justify-between rounded bg-white px-2 py-1 text-left text-[14px] text-[#6b7280] ${hasSelection
            ? "border border-[#10b981] shadow-[0_0_0_1px_rgba(16,185,129,0.08)]"
            : "border border-[#cfd6df]"
            }`}
        >
          <span className="flex flex-1 flex-wrap gap-1">
            {displayValues.map((item) => {
              const isPlaceholder = item === EVENT_CENTER_EVENT_PLACEHOLDER;
              const displayItem = getOptionLabel(item);
              return (
                <span
                  key={getOptionKey(item)}
                  className={`flex h-6 items-center gap-1 rounded px-2 text-[12px] ${isPlaceholder ? "text-[#98a2b3]" : "border border-[#d7dee8] bg-[#f5f7fa] text-[#6b7280]"
                    }`}
                >
                  <span className="max-w-[150px] truncate">{displayItem}</span>
                  {!isPlaceholder ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        onChange(values.filter((value) => getOptionKey(value) !== getOptionKey(item)));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          onChange(values.filter((value) => getOptionKey(value) !== getOptionKey(item)));
                        }
                      }}
                      className="text-[#98a2b3]"
                    >
                      ×
                    </span>
                  ) : null}
                </span>
              );
            })}
          </span>
          <div className="ml-2 flex items-center gap-2 pt-1">
            {values.length > 0 ? (
              <>
                <span className="h-4 w-px bg-[#d7dee8]" />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange([]);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange([]);
                    }
                  }}
                  className="pt-[1px] text-[#98a2b3]"
                >
                  ×
                </span>
              </>
            ) : null}
            <ChevronDown className={`h-4 w-4 text-[#b3bac5] transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </div>
        </button>
      </div>
      {isOpen ? (
        <div className="absolute left-[90px] top-full z-50 mt-2 w-[calc(100%-90px)] min-w-[260px] overflow-hidden rounded border border-[#dbe4ee] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
          <div className="border-b border-[#edf1f5] p-3">
            <div className="flex h-[36px] items-center gap-2 rounded border border-[#d7dee8] bg-[#f8fafc] px-3">
              <FiSearch className="h-4 w-4 text-[#98a2b3]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Rechercher un evenement"
                className="h-full w-full border-none bg-transparent text-[14px] text-[#495568] outline-none placeholder:text-[#98a2b3]"
              />
            </div>
          </div>
          {isLoading ? (
            <div className="px-5 py-4 text-[14px] text-[#666]">Chargement...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-5 py-4 text-[14px] text-[#666]">Aucun evenement</div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto py-1">
              {filteredOptions.map((option) => {
                const optionKey = getOptionKey(option);
                const isSelected = values.some((value) => getOptionKey(value) === optionKey);
                return (
                  <button
                    key={optionKey}
                    type="button"
                    onClick={() =>
                      onChange(
                        isSelected
                          ? values.filter((value) => getOptionKey(value) !== optionKey)
                          : [...values, option]
                      )
                    }
                    className={`flex w-full items-center justify-between px-5 py-4 text-left text-[14px] hover:bg-[#f5f9ff] ${isSelected ? "font-semibold text-[#10b981]" : "text-[#666]"
                      }`}
                  >
                    <span>{getOptionLabel(option)}</span>
                    <span className={`${isSelected ? "text-[#10b981]" : "text-transparent"}`}>✓</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function EventCenterDatePicker({ isOpen, onToggle, onClose, startDate, endDate, onChange }) {
  const { t } = useTranslation();
  const [activeEdge, setActiveEdge] = useState("start");
  const [visibleMonth, setVisibleMonth] = useState(new Date(startDate.getFullYear(), startDate.getMonth(), 1));

  useEffect(() => {
    setVisibleMonth(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  }, [startDate]);

  const nextMonth = addMonths(visibleMonth, 1);
  const leftMonthDays = buildMonthGrid(visibleMonth);
  const rightMonthDays = buildMonthGrid(nextMonth);

  const applyQuickRange = (range) => {
    const baseDate = new Date();

    if (range === "Today") {
      onChange({ startDate: startOfDay(baseDate), endDate: endOfDay(baseDate) });
      setVisibleMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
      return;
    }
    if (range === "Yesterday") {
      const yesterday = new Date(baseDate);
      yesterday.setDate(yesterday.getDate() - 1);
      onChange({ startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) });
      setVisibleMonth(new Date(yesterday.getFullYear(), yesterday.getMonth(), 1));
      return;
    }
    if (range === "This week") {
      onChange({ startDate: startOfWeek(baseDate), endDate: endOfWeek(baseDate) });
      setVisibleMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
      return;
    }
    if (range === "Last Week") {
      const lastWeekDate = new Date(baseDate);
      lastWeekDate.setDate(lastWeekDate.getDate() - 7);
      onChange({ startDate: startOfWeek(lastWeekDate), endDate: endOfWeek(lastWeekDate) });
      setVisibleMonth(new Date(lastWeekDate.getFullYear(), lastWeekDate.getMonth(), 1));
      return;
    }
    if (range === "This Month") {
      onChange({ startDate: startOfMonth(baseDate), endDate: endOfMonth(baseDate) });
      setVisibleMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
      return;
    }

    const lastMonthDate = new Date(baseDate.getFullYear(), baseDate.getMonth() - 1, 1);
    onChange({ startDate: startOfMonth(lastMonthDate), endDate: endOfMonth(lastMonthDate) });
    setVisibleMonth(new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth(), 1));
  };

  const handleDaySelect = (date) => {
    const selectedDate = new Date(date);

    if (activeEdge === "start") {
      const nextStartDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        startDate.getHours(),
        startDate.getMinutes(),
        startDate.getSeconds()
      );
      onChange({
        startDate: nextStartDate,
        endDate: nextStartDate > endDate ? endOfDay(selectedDate) : endDate,
      });
      setActiveEdge("end");
      return;
    }

    const nextEndDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      endDate.getHours(),
      endDate.getMinutes(),
      endDate.getSeconds()
    );
    if (nextEndDate < startDate) {
      onChange({ startDate: startOfDay(selectedDate), endDate: endOfDay(selectedDate) });
    } else {
      onChange({ startDate, endDate: nextEndDate });
    }
    setActiveEdge("start");
  };

  const handleDateInputChange = (edge, value) => {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return;

    const sourceDate = edge === "start" ? startDate : endDate;
    const nextDate = new Date(year, month - 1, day, sourceDate.getHours(), sourceDate.getMinutes(), sourceDate.getSeconds());

    if (edge === "start") {
      onChange({
        startDate: nextDate,
        endDate: nextDate > endDate ? endOfDay(nextDate) : endDate,
      });
    } else {
      onChange({
        startDate: nextDate < startDate ? startOfDay(nextDate) : startDate,
        endDate: nextDate,
      });
    }

    setVisibleMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
  };

  const handleTimeInputChange = (edge, value) => {
    const [hours, minutes, seconds] = value.split(":").map(Number);
    if ([hours, minutes, seconds].some((part) => Number.isNaN(part))) return;

    const sourceDate = edge === "start" ? startDate : endDate;
    const nextDate = new Date(sourceDate);
    nextDate.setHours(hours, minutes, seconds);

    if (edge === "start") {
      onChange({ startDate: nextDate, endDate });
    } else {
      onChange({ startDate, endDate: nextDate });
    }
  };

  const renderMonth = (monthDate, days, side) => (
    <div>
      <div className="mb-3 flex items-center justify-between text-[13px] text-[#5d6a78]">
        <button
          type="button"
          onClick={() => side === "left" && setVisibleMonth((prev) => addMonths(prev, -1))}
          className={`px-1 ${side === "left" ? "text-[#5d6a78]" : "invisible"}`}
        >
          {"<<"}
        </button>
        <span className="text-[16px]">{`${monthDate.getFullYear()} ${t(`eventCenter.months.${monthDate.getMonth()}`)}`}</span>
        <button
          type="button"
          onClick={() => side === "right" && setVisibleMonth((prev) => addMonths(prev, 1))}
          className={`px-1 ${side === "right" ? "text-[#5d6a78]" : "invisible"}`}
        >
          {">>"}
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-2 text-center text-[12px] text-[#6b7280]">
        {EVENT_CENTER_WEEK_DAYS.map((day, index) => (
          <span key={`${side}-${day}`}>{t(`eventCenter.weekdays.${index}`)}</span>
        ))}
        {days.map((day) => {
          const isMuted = day.getMonth() !== monthDate.getMonth();
          const isStart = isSameDay(day, startDate);
          const isEnd = isSameDay(day, endDate);
          const isInRange = day >= startOfDay(startDate) && day <= endOfDay(endDate);
          return (
            <button
              key={`${side}-${day.toISOString()}`}
              type="button"
              onClick={() => handleDaySelect(day)}
              className={`mx-auto grid h-7 w-7 place-items-center rounded-full text-[13px] ${isStart || isEnd
                ? "bg-[#10b981] text-white"
                : isInRange
                  ? "bg-[#eef5ff] text-[#2f4a63]"
                  : isMuted
                    ? "text-[#b2bcc9]"
                    : "text-[#485668]"
                }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  const handleClear = () => {
    const { startDate: nextStartDate, endDate: nextEndDate } = createDefaultEventCenterRange();
    onChange({ startDate: nextStartDate, endDate: nextEndDate });
    setActiveEdge("start");
    setVisibleMonth(new Date(nextStartDate.getFullYear(), nextStartDate.getMonth(), 1));
  };

  return (
    <div className="relative min-w-[360px] max-w-full">
      <div className="flex items-center gap-3">
        <span className="text-[14px] text-[#555]">{t("eventCenter.date")}</span>
        <button
          type="button"
          onClick={onToggle}
          className="flex h-[32px] min-w-[320px] items-center gap-3 rounded border border-[#cfd6df] bg-white px-3 text-[14px] text-[#666]"
        >
          <Clock3 className="h-4 w-4 text-[#8ea0b4]" />
          <span>{formatEventCenterDateTime(startDate)}</span>
          <span className="text-[#a8b2be]">-</span>
          <span>{formatEventCenterDateTime(endDate)}</span>
          <X className="ml-auto h-3.5 w-3.5 text-[#9aa7b6]" />
        </button>
      </div>
      <div className="ml-[57px] mt-1 flex items-center gap-1 text-[12px] text-[#d69b2f]">
        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#ffcc7a] text-[10px] text-white">!</span>
        <span>{t("eventCenter.maximum_one_month")}</span>
      </div>
      {isOpen ? (
        <div className="absolute right-0 top-full z-50 mt-3 w-[min(740px,calc(100vw-3rem))] max-w-[calc(100vw-3rem)] rounded border border-[#dbe4ee] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
          <div className="flex">
            <div className="w-[100px] shrink-0 border-r border-[#e7edf4] bg-[#fafbfd] py-1">
              {EVENT_CENTER_QUICK_RANGES.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => applyQuickRange(item)}
                  className="block w-full whitespace-nowrap px-3 py-2 text-left text-[14px] text-[#586373] hover:bg-[#f0f6ff]"
                >
                  {t(`eventCenter.quickRange.${item}`)}
                </button>
              ))}
            </div>
            <div className="min-w-0 flex-1 p-4">
              <div className="mb-4 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_20px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2">
                <input
                  value={formatEventCenterDate(startDate)}
                  onChange={(event) => handleDateInputChange("start", event.target.value)}
                  onFocus={() => setActiveEdge("start")}
                  className="flex h-[32px] min-w-0 w-full items-center rounded border border-[#d8e0ea] px-3 text-[14px] text-[#666] outline-none"
                />
                <input
                  value={formatEventCenterTime(startDate)}
                  onChange={(event) => handleTimeInputChange("start", event.target.value)}
                  onFocus={() => setActiveEdge("start")}
                  className="flex h-[32px] min-w-0 w-full items-center rounded border border-[#d8e0ea] px-3 text-[14px] text-[#666] outline-none"
                />
                <span className="text-center text-[#9aa7b6]">{">"}</span>
                <input
                  value={formatEventCenterDate(endDate)}
                  onChange={(event) => handleDateInputChange("end", event.target.value)}
                  onFocus={() => setActiveEdge("end")}
                  className="flex h-[32px] min-w-0 w-full items-center rounded border border-[#d8e0ea] px-3 text-[14px] text-[#666] outline-none"
                />
                <input
                  value={formatEventCenterTime(endDate)}
                  onChange={(event) => handleTimeInputChange("end", event.target.value)}
                  onFocus={() => setActiveEdge("end")}
                  className="flex h-[32px] min-w-0 w-full items-center rounded border border-[#d8e0ea] px-3 text-[14px] text-[#666] outline-none"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {renderMonth(visibleMonth, leftMonthDays, "left")}
                {renderMonth(nextMonth, rightMonthDays, "right")}
              </div>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button type="button" onClick={handleClear} className="text-[14px] text-[#9a6d2b]">
                  {t("eventCenter.clear")}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded border border-[#d7dee8] bg-white px-4 py-1.5 text-[14px] text-[#556271]"
                >
                  {t("eventCenter.ok")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EventCenterPreviewGoogleMap({ google, row }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const infoWindowRef = useRef(null);
  const latitude = Number(row?.raw?.lat);
  const longitude = Number(row?.raw?.lng);
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!google || !mapRef.current || !hasValidCoordinates) {
      return undefined;
    }

    const position = { lat: latitude, lng: longitude };
    const popupContent = `
      <div style="min-width:170px;font-size:12px;color:#475569;line-height:1.5;">
        <div><strong>${t("eventCenter.device")}:</strong> ${row.plateNumber}</div>
        <div><strong>${t("eventCenter.event_name")}:</strong> ${row.eventName}</div>
        <div><strong>${t("eventCenter.time")}:</strong> ${row.time}</div>
        <div><strong>LatLng:</strong> ${latitude}, ${longitude}</div>
        <div><strong>${t("eventCenter.speed")}:</strong> ${row.speed}</div>
      </div>
    `;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center: position,
        zoom: 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
    } else {
      mapInstanceRef.current.setCenter(position);
    }

    if (markerInstanceRef.current) {
      markerInstanceRef.current.setMap(null);
    }

    markerInstanceRef.current = new google.maps.Marker({
      position,
      map: mapInstanceRef.current,
      title: row.plateNumber,
    });

    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    infoWindowRef.current.setContent(popupContent);
    infoWindowRef.current.open({
      anchor: markerInstanceRef.current,
      map: mapInstanceRef.current,
      shouldFocus: false,
    });

    return () => {
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setMap(null);
      }
    };
  }, [google, hasValidCoordinates, latitude, longitude, row]);

  if (!hasValidCoordinates) {
    return (
      <div className="grid h-[360px] place-items-center border border-[#e8edf3] bg-[#f8fafc] text-[14px] text-[#7b8797]">
        {t("eventCenter.no_position")}
      </div>
    );
  }

  return (
    <div ref={mapRef} className="h-[360px] overflow-hidden border border-[#e8edf3]" />
  );
}

function EventCenterOperateModal({ modalState, onClose }) {
  const { t } = useTranslation();
  const [previewTab, setPreviewTab] = useState("video");
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(8);
  const [isReplayPaused, setIsReplayPaused] = useState(true);
  const [loadReplayData, { data: replayData, loading: replayLoading, error: replayError }] = useLazyQuery(GET_REPLAY_DATA_CAMERA, {
    fetchPolicy: "network-only",
  });
  const row = modalState?.row;
  const previewVideoLinks = normalizeEventCenterLinks(row?.raw?.videoLinks);
  const previewImages = normalizeEventCenterLinks(row?.raw?.imageLinks);
  const displayPreviewImages = previewImages;
  const previewVideoUrl = previewVideoLinks[0] || "";
  const hasMediaContent = previewVideoLinks.length > 0 || displayPreviewImages.length > 0;
  const replayAlertId = getEventCenterReplayAlertId(row?.raw);
  const replayCamera = Array.isArray(replayData?.getReplayDataCamera) ? replayData.getReplayDataCamera[0] : replayData?.getReplayDataCamera;
  const replayPoints = Array.isArray(replayCamera?.point) ? replayCamera.point : [];
  const replayDistance = typeof replayCamera?.distanceTravelledKms === "number" ? replayCamera.distanceTravelledKms.toFixed(3) : null;
  const replayMaxIndex = Math.max(replayPoints.length - 1, 0);
  const replaySliderValue = replayMaxIndex > 0 ? Math.round((replayIndex / replayMaxIndex) * 100) : 0;

  useEffect(() => {
    if (modalState?.type === "preview") {
      setPreviewTab(previewVideoLinks.length ? "video" : "picture");
      setPreviewImageIndex(0);
    }
  }, [modalState, previewVideoLinks.length]);

  useEffect(() => {
    if (modalState?.type !== "track") {
      return;
    }

    const uniqueid = row?.raw?.uniqueId;
    if (!uniqueid || !replayAlertId) {
      return;
    }

    loadReplayData({
      variables: {
        uniqueid,
        alert_id: replayAlertId,
      },
    });
  }, [loadReplayData, modalState?.type, replayAlertId, row?.raw?.uniqueId]);

  useEffect(() => {
    setReplayIndex(0);
    setIsReplayPaused(true);
  }, [replayAlertId, replayPoints.length, row?.raw?.uniqueId]);

  useEffect(() => {
    if (modalState?.type !== "track" || isReplayPaused || replayPoints.length <= 1) {
      return undefined;
    }

    if (replayIndex >= replayPoints.length - 1) {
      setIsReplayPaused(true);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setReplayIndex((currentIndex) => Math.min(currentIndex + 1, replayPoints.length - 1));
    }, Math.max(120, Math.round(1000 / replaySpeed)));

    return () => window.clearTimeout(timeoutId);
  }, [isReplayPaused, modalState?.type, replayIndex, replayPoints.length, replaySpeed]);

  if (!modalState) return null;

  const { type } = modalState;
  const isPreview = type === "preview";
  const currentPreviewImage = displayPreviewImages[previewImageIndex] || displayPreviewImages[0];
  const latitude = Number(row?.raw?.lat);
  const longitude = Number(row?.raw?.lng);
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const formattedLatLng = hasValidCoordinates ? `${latitude}, ${longitude}` : "-";
  const attachmentZipUrl = row?.raw?.fileName
    ? `https://dev.api.lewootrack.com/api/download-zip/v2?fileName=${encodeURIComponent(row.raw.fileName)}`
    : "";
  const handleReplayPlayPause = (nextPlaying) => {
    if (replayPoints.length <= 1) {
      return;
    }

    if (replayIndex >= replayPoints.length - 1 && nextPlaying) {
      setReplayIndex(0);
    }

    setIsReplayPaused(!nextPlaying);
  };
  const handleReplaySpeedChange = (nextSpeed) => {
    setReplaySpeed(nextSpeed);
  };
  const handleReplaySliderChange = (_, value) => {
    if (replayPoints.length <= 1) {
      setReplayIndex(0);
      return;
    }

    const nextIndex = Math.min(
      replayPoints.length - 1,
      Math.max(0, Math.round((Number(value) / 100) * (replayPoints.length - 1)))
    );
    setReplayIndex(nextIndex);
    setIsReplayPaused(true);
  };
  const handleAttachmentExport = () => {
    if (!attachmentZipUrl) {
      return;
    }

    window.open(attachmentZipUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-[1120px] rounded-xl bg-white shadow-[0_18px_60px_rgba(15,23,42,0.22)]">
        <div className="flex items-center justify-between border-b border-[#e8edf3] px-5 py-4">
          <div className="text-[18px] font-medium text-[#2f3b49]">{isPreview ? t("eventCenter.event_details") : t("eventCenter.track_playback")}</div>
          <button type="button" onClick={onClose} className="rounded p-1 text-[#94a0b2] hover:bg-[#f4f7fb]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {isPreview ? (
          <div className="px-5 py-4">
            <div className="grid grid-cols-1 gap-4 border-b border-[#e8edf3] pb-5 text-[14px] text-[#3e4b59] lg:grid-cols-[1.05fr_1fr_1.05fr]">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-[#10b981]/15 text-[#10b981]">
                    <div className="h-3 w-3 bg-[linear-gradient(90deg,#10b981_0_25%,transparent_25_35%,#10b981_35_60%,transparent_60_70%,#10b981_70_100%)]" />
                  </div>
                  <span>{row.raw?.uniqueId || row.plateNumber}</span>
                  <Copy className="h-3.5 w-3.5 text-[#9aa7b6]" />
                </div>
                <div className="flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-[#10b981]" />
                  <span>{row.speed}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-[#10b981]" />
                  <span>{row.belongTo}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#10b981]" />
                  <span>{row.address}</span>
                </div>
              </div>

              <div className="grid grid-cols-[1fr_120px] gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-[#10b981]" />
                    <span>{row.eventName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock3 className="h-4 w-4 text-[#10b981]" />
                    <span>{row.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPinned className="h-4 w-4 text-[#10b981]" />
                    <span>{formattedLatLng}</span>
                  </div>
                </div>
                <div className="h-[118px] rounded bg-[#eff3f7]" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[#10b981]" />
                  <span>{t("eventCenter.driver_name")}: {row.raw?.driverName || "-"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#10b981]" />
                  <span>{t("eventCenter.contact")}: {row.raw?.contactNumber || "-"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-[#10b981]" />
                  <span>{t("eventCenter.id_number")}: {row.raw?.tripId || "-"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-[#10b981]" />
                  <span>{t("eventCenter.plate_number")}: {row.plateNumber}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-0 lg:grid-cols-2">
              <div className="overflow-hidden">
                {!hasMediaContent ? (
                  <div className="grid h-[320px] place-items-center rounded bg-[#f3f4f6] px-6 text-center text-[14px] text-[#6b7280]">
                    {t("eventCenter.no_media")}
                  </div>
                ) : previewTab === "video" ? (
                  <div className="relative h-[320px] bg-black">
                    {previewVideoUrl ? (
                      <video
                        key={previewVideoUrl}
                        src={previewVideoUrl}
                        controls
                        preload="metadata"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="grid h-full place-items-center px-6 text-center text-[14px] text-white/70">
                        {t("eventCenter.no_media")}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative h-[320px] bg-[#111827]">
                    {currentPreviewImage ? (
                      <img src={currentPreviewImage} alt={`Event attachment ${previewImageIndex + 1}`} className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full place-items-center px-6 text-center text-[14px] text-white/70">
                        {t("eventCenter.no_media")}
                      </div>
                    )}
                    <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/55 to-transparent px-4 py-3 text-[12px] text-white">
                      <span>{t("eventCenter.picture_count", { current: previewImageIndex + 1, total: displayPreviewImages.length })}</span>
                      <span>{row.time}</span>
                    </div>
                    {displayPreviewImages.length > 1 ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewImageIndex((prev) =>
                              prev === 0 ? displayPreviewImages.length - 1 : prev - 1
                            )
                          }
                          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-2 text-white"
                          title={t("eventCenter.previous_picture")}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewImageIndex((prev) =>
                              prev === displayPreviewImages.length - 1 ? 0 : prev + 1
                            )
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-2 text-white"
                          title={t("eventCenter.next_picture")}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </>
                    ) : null}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-4 py-3">
                      <div className="flex items-center gap-2 overflow-x-auto">
                        {displayPreviewImages.map((image, index) => (
                          <button
                            key={`${image}-${index}`}
                            type="button"
                            onClick={() => setPreviewImageIndex(index)}
                            className={`h-14 w-20 shrink-0 overflow-hidden rounded border-2 ${index === previewImageIndex ? "border-[#10b981]" : "border-transparent"
                              }`}
                          >
                            <img src={image} alt={`Attachment thumbnail ${index + 1}`} className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between border border-t-0 border-[#e8edf3] px-4 py-2">
                  <div className="flex items-center gap-8">
                    <button
                      type="button"
                      onClick={() => setPreviewTab("video")}
                      className={`pb-2 text-[14px] ${previewTab === "video" ? "border-b-2 border-[#10b981] text-[#10b981]" : "text-[#4b5563]"}`}
                    >
                      {t("eventCenter.video")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewTab("picture")}
                      className={`pb-2 text-[14px] ${previewTab === "picture" ? "border-b-2 border-[#10b981] text-[#10b981]" : "text-[#4b5563]"}`}
                    >
                      {t("eventCenter.picture")}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleAttachmentExport}
                    disabled={!attachmentZipUrl}
                    className={`flex items-center gap-2 rounded border px-3 py-1.5 text-[13px] ${attachmentZipUrl
                      ? "border-[#cfe6dc] bg-[#f2fbf6] text-[#3b5b4f] hover:bg-[#e8f6ee]"
                      : "border-[#d7dee8] bg-white text-[#7b8794] opacity-50"
                      } disabled:cursor-not-allowed`}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    {t("eventCenter.attachment_export")}
                  </button>
                </div>
              </div>

              <GoogleMapContainer loadingComponent={<div className="grid h-[360px] place-items-center border border-[#e8edf3] bg-[#f8fafc] text-[14px] text-[#7b8797]">{t("eventCenter.loading_map")}</div>}>
                {(google) => <EventCenterPreviewGoogleMap google={google} row={row} />}
              </GoogleMapContainer>
            </div>
          </div>
        ) : (
          <div>
            {false ? (<div className="relative h-[420px] overflow-hidden border-b border-[#d7dee8] bg-[#77d6e7]">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(0deg, rgba(255,255,255,0.15), rgba(255,255,255,0.15)), radial-gradient(circle at 18% 38%, rgba(255,255,255,0.85) 0 12%, transparent 12.5%), radial-gradient(circle at 42% 28%, rgba(255,255,255,0.82) 0 11%, transparent 11.5%), radial-gradient(circle at 58% 40%, rgba(180,238,206,0.9) 0 9%, transparent 9.5%), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.82) 0 10%, transparent 10.5%), linear-gradient(90deg, rgba(60,60,60,0.2) 1px, transparent 1px), linear-gradient(rgba(60,60,60,0.2) 1px, transparent 1px)",
                  backgroundSize: "auto, auto, auto, auto, auto, 120px 120px, 120px 120px",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute left-3 top-3 overflow-hidden rounded border border-[#9bb3be] bg-white shadow-sm">
                <button className="grid h-10 w-10 place-items-center border-b border-[#d7dee8] text-[30px] leading-none text-[#1f2937]">+</button>
                <button className="grid h-10 w-10 place-items-center text-[30px] leading-none text-[#1f2937]">-</button>
              </div>
              <div className="absolute bottom-4 left-1/2 w-[72%] -translate-x-1/2 rounded bg-[#516b78]/90 px-5 py-4 text-white">
                <div className="mb-3 flex items-center gap-4">
                  <button className="grid h-6 w-6 place-items-center rounded-full border border-white/70 text-[12px]">▷</button>
                  <div className="relative h-1 flex-1 rounded bg-white/40">
                    <div className="absolute left-0 top-0 h-1 w-[12%] rounded bg-white" />
                    <div className="absolute left-[12%] top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border-2 border-white bg-[#10b981]" />
                  </div>
                  <span>-</span>
                </div>
              </div>
              <div className="absolute bottom-4 right-3 flex items-end gap-1 text-[#334155]">
                <div className="border border-[#94a3b8] bg-white/80 px-2 py-1 text-[11px]">1000 km</div>
                <div className="border border-[#94a3b8] bg-white/80 px-2 py-1 text-[11px]">1000 mi</div>
              </div>
            </div>) : (
              <div className="relative">
                <GoogleMapContainer loadingComponent={<div className="grid h-[420px] place-items-center border-b border-[#d7dee8] bg-[#f8fafc] text-[14px] text-[#7b8797]">Loading map...</div>}>
                  {(google) => (
                    <EventCenterReplayGoogleMap
                      google={google}
                      points={replayPoints}
                      activeIndex={replayIndex}
                      vehicleType={row?.raw?.vehicleType || row?.raw?.type}
                      uniqueId={row?.raw?.uniqueId}
                      replayInterval={Math.max(120, Math.round(1000 / replaySpeed))}
                    />
                  )}
                </GoogleMapContainer>
                {replayPoints.length > 0 ? (
                  <div className="pointer-events-auto absolute bottom-4 left-1/2 z-20 flex w-[min(420px,calc(100%-40px))] -translate-x-1/2 flex-col gap-2 rounded-[22px] border border-white/70 bg-white/94 px-3 py-2 shadow-[0_18px_44px_rgba(15,23,42,0.16)] backdrop-blur-sm">
                    <div className="flex items-center justify-between gap-3">
                      <ReplayControlPanel
                        togglePlay={!isReplayPaused}
                        speed={replaySpeed}
                        onPlayPause={handleReplayPlayPause}
                        speedFactor={handleReplaySpeedChange}
                      />
                      <div className="text-[12px] text-[#64748b]">
                        {replayIndex + 1} / {replayPoints.length}
                      </div>
                    </div>
                    <div className="px-1 pb-1">
                      <Slider
                        value={replaySliderValue}
                        onChange={handleReplaySliderChange}
                        min={0}
                        max={100}
                        step={1}
                        size="small"
                        sx={{
                          padding: "6px 0",
                          color: "#2f80ed",
                          height: 4,
                          "& .MuiSlider-rail": {
                            backgroundColor: "#d9e7fb",
                            opacity: 1,
                            height: 4,
                          },
                          "& .MuiSlider-track": {
                            border: "none",
                            height: 4,
                          },
                          "& .MuiSlider-thumb": {
                            width: 14,
                            height: 14,
                            backgroundColor: "#2f80ed",
                            boxShadow: "0 6px 14px rgba(47,128,237,0.28)",
                          },
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>)}

            <div className="border-b border-[#d7dee8] px-3 py-2 text-[14px] text-[#10b981]">Detail</div>
            <div className="min-h-[240px] bg-white">
              <div className="flex items-center justify-between border-b border-[#eef2f7] px-3 py-2 text-[12px] text-[#64748b]">
                <span>{row?.raw?.uniqueId || "-"}</span>
                <span>{replayDistance ? `Distance travelled: ${replayDistance} km` : "Distance travelled: -"}</span>
              </div>
              <div className="grid grid-cols-[60px_160px_190px_190px_190px_190px_1fr] border-b border-[#e8edf3] px-3 py-2 text-[13px] font-semibold text-[#1f2937]">
                <span>Index</span>
                <span>Time</span>
                <span>Longitude</span>
                <span>Latitude</span>
                <span>Speed(km/h)</span>
                <span>Direction</span>
                <span>Address</span>
              </div>
              {!row?.raw?.uniqueId || !replayAlertId ? (
                <div className="grid h-[190px] place-items-center text-[18px] text-[#6b7280]">No Data</div>
              ) : replayLoading ? (
                <div className="grid h-[190px] place-items-center text-[18px] text-[#6b7280]">Loading...</div>
              ) : replayError ? (
                <div className="grid h-[190px] place-items-center text-center text-[18px] text-[#6b7280]">Unable to load replay data</div>
              ) : replayPoints.length === 0 ? (
                <div className="grid h-[190px] place-items-center text-[18px] text-[#6b7280]">No Data</div>
              ) : (
                <div className="max-h-[260px] overflow-y-auto">
                  {replayPoints.map((point, index) => (
                    <div
                      key={`${point?.ts || "point"}-${index}`}
                      className={`grid grid-cols-[60px_160px_190px_190px_190px_190px_1fr] border-b border-[#eef2f7] px-3 py-2 text-[12px] text-[#334155] ${index === replayIndex ? "bg-[#ecfdf5]" : ""}`}
                    >
                      <span>{index + 1}</span>
                      <span>{formatEventCenterReplayTimestamp(point?.ts)}</span>
                      <span>{point?.lng ?? "-"}</span>
                      <span>{point?.lat ?? "-"}</span>
                      <span>{point?.speed ?? "-"}</span>
                      <span>{getEventCenterReplayDirection(point)}</span>
                      <span>{point?.address || "-"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function EventCenterClassicView() {
  const { t } = useTranslation();
  const location = useLocation();
  const isEventCenterV2Route = String(location.pathname || "").toLowerCase().includes("/event-center-v2");
  const layoutGridClass = isEventCenterV2Route
    ? "grid items-stretch gap-3 xl:grid-cols-[370px_minmax(0,1fr)]"
    : "grid items-stretch gap-3 xl:grid-cols-[300px_minmax(0,1fr)]";
  const defaultRange = useMemo(() => createDefaultEventCenterRange(), []);
  const [selectedDevice, setSelectedDevice] = useState(EVENT_CENTER_DEVICE_PLACEHOLDER);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [operateModal, setOperateModal] = useState(null);
  const [page, setPage] = useState(1);
  const [isDeviceQueryApplied, setIsDeviceQueryApplied] = useState(false);
  const [isAutoOpenApplied, setIsAutoOpenApplied] = useState(false);
  const [isAutoOpenMonthRangeApplied, setIsAutoOpenMonthRangeApplied] = useState(false);

  const [loadEventCenter, { data, loading, error }] = useLazyQuery(GET_DATA_EVENT_CENTER, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const { data: alertTypeData, loading: alertTypesLoading } = useQuery(GET_ALERT_TYPE_CAMERA, {
    fetchPolicy: "cache-and-network",
  });
  const { devices = [], loading: devicesLoading } = useDevices({
    useV2: isEventCenterV2Route,
    filters: isEventCenterV2Route ? { isAst: true } : undefined,
  });
  const eventCenterQueryOptions = useMemo(
    () =>
      isEventCenterV2Route
        ? { forceIsAst: true, omitClientIdWhenDeviceSelected: true }
        : {},
    [isEventCenterV2Route]
  );

  const filters = useMemo(
    () => ({ selectedDevice, selectedEvents, startDate, endDate }),
    [selectedDevice, selectedEvents, startDate, endDate]
  );
  const requestedDeviceQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("device");
  }, [location.search]);
  const autoOpenMode = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const trackAutoOpenValue = params.get("isEvent") ?? params.get("isevent");
    const previewAutoOpenValue = params.get("isopen") ?? params.get("isOpen");

    if (shouldAutoOpenEventCenterModal(trackAutoOpenValue)) return "track";
    if (shouldAutoOpenEventCenterModal(previewAutoOpenValue)) return "preview";
    return null;
  }, [location.search]);

  useEffect(() => {
    setIsDeviceQueryApplied(false);
  }, [requestedDeviceQuery]);

  useEffect(() => {
    setIsAutoOpenApplied(false);
    setIsAutoOpenMonthRangeApplied(false);
  }, [location.search]);

  useEffect(() => {
    if (!autoOpenMode) return;
    if (isAutoOpenMonthRangeApplied) return;

    const autoOpenRange = createAutoOpenEventCenterRange();
    setStartDate(autoOpenRange.startDate);
    setEndDate(autoOpenRange.endDate);
    setPage(1);
    setIsAutoOpenMonthRangeApplied(true);
  }, [autoOpenMode, isAutoOpenMonthRangeApplied]);

  const rows = useMemo(
    () => (data?.getDataEventCenter || []).map((item, index) => mapEventCenterRow(item, index)),
    [data]
  );
  const eventOptions = useMemo(() => alertTypeData?.getAlertTypeCamera || [], [alertTypeData]);
  const deviceOptions = useMemo(
    () => [
      EVENT_CENTER_DEVICE_PLACEHOLDER,
      ...devices
        .filter((device) => device?.uniqueId)
        .sort((firstDevice, secondDevice) =>
          (firstDevice?.vehicleNumber || firstDevice?.uniqueId || "").localeCompare(
            secondDevice?.vehicleNumber || secondDevice?.uniqueId || ""
          )
        ),
    ],
    [devices]
  );
  const currentClientName = useMemo(() => {
    const storedClientName = getLocalStorage("username");
    return typeof storedClientName === "string" && storedClientName.trim().length > 0
      ? storedClientName.trim()
      : "My Clients";
  }, []);
  const totalDeviceCount = useMemo(
    () => devices.filter((device) => device?.uniqueId).length,
    [devices]
  );
  const selectedVehicleEntry = useMemo(() => {
    if (!selectedDevice || selectedDevice === EVENT_CENTER_DEVICE_PLACEHOLDER) {
      return null;
    }

    return devices.find((device) => device?.uniqueId === selectedDevice) || null;
  }, [devices, selectedDevice]);
  const activeDeviceCount =
    selectedDevice && selectedDevice !== EVENT_CENTER_DEVICE_PLACEHOLDER
      ? 1
      : totalDeviceCount;
  const myUserEntries = useMemo(() => {
    const normalizedSearch = userSearchTerm.trim().toLowerCase();
    const userEntry = {
      id: "my-user",
      name: currentClientName,
      label:
        totalDeviceCount > 0
          ? `${currentClientName}(${activeDeviceCount}/${totalDeviceCount})`
          : currentClientName,
    };

    if (!normalizedSearch) {
      return [userEntry];
    }

    return [userEntry].filter((entry) => entry.name.toLowerCase().includes(normalizedSearch));
  }, [activeDeviceCount, currentClientName, totalDeviceCount, userSearchTerm]);

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paginatedRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const visiblePages = useMemo(() => {
    const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
    const endPage = Math.min(totalPages, startPage + 4);
    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [page, totalPages]);

  useEffect(() => {
    const handlePointerDown = () => setOpenMenu(null);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    loadEventCenter({ variables: normalizeEventCenterFilters(filters, eventCenterQueryOptions) });
  }, [eventCenterQueryOptions, filters, loadEventCenter]);

  useEffect(() => {
    setPage(1);
  }, [selectedDevice, selectedEvents, startDate, endDate]);

  useEffect(() => {
    if (!requestedDeviceQuery) return;
    if (isDeviceQueryApplied) return;

    const requestedToken = normalizeEventCenterDeviceToken(requestedDeviceQuery);
    if (!requestedToken) return;

    const matchedDevice = devices.find((device) => {
      const candidates = [
        device?.uniqueId,
        device?.vehicleNumber,
        device?.imei,
        device?.imei_num,
      ];

      return candidates.some(
        (candidate) => normalizeEventCenterDeviceToken(candidate) === requestedToken
      );
    });

    if (!matchedDevice?.uniqueId) {
      // Device query provided but no match found in loaded device list:
      // mark as resolved so auto-open flow is not blocked forever.
      if (Array.isArray(devices) && devices.length > 0) {
        setIsDeviceQueryApplied(true);
      }
      return;
    }
    if (selectedDevice === matchedDevice.uniqueId) {
      setIsDeviceQueryApplied(true);
      return;
    }

    setSelectedDevice(matchedDevice.uniqueId);
    setIsDeviceQueryApplied(true);
  }, [requestedDeviceQuery, devices, selectedDevice, isDeviceQueryApplied]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!autoOpenMode) return;
    if (isAutoOpenApplied) return;
    if (loading) return;
    if (!isAutoOpenMonthRangeApplied) return;
    if (requestedDeviceQuery && Array.isArray(devices) && devices.length > 0 && !isDeviceQueryApplied) return;
    if (!Array.isArray(rows) || rows.length === 0) return;

    const latestRow = getLatestEventCenterRow(rows);
    if (!latestRow) return;

    setOperateModal({ type: autoOpenMode, row: latestRow });
    setIsAutoOpenApplied(true);
  }, [
    autoOpenMode,
    isAutoOpenApplied,
    loading,
    isAutoOpenMonthRangeApplied,
    requestedDeviceQuery,
    devices,
    isDeviceQueryApplied,
    rows,
  ]);

  const isAutoOpenPending =
    !!autoOpenMode &&
    !isAutoOpenApplied &&
    !operateModal &&
    (
      !isAutoOpenMonthRangeApplied ||
      loading ||
      (requestedDeviceQuery && devicesLoading) ||
      (requestedDeviceQuery && Array.isArray(devices) && devices.length > 0 && !isDeviceQueryApplied)
    );

  const handleSearch = () => {
    loadEventCenter({ variables: normalizeEventCenterFilters(filters, eventCenterQueryOptions) });
    setOpenMenu(null);
  };

  const handleReset = () => {
    const resetRange = createDefaultEventCenterRange();
    setSelectedDevice(EVENT_CENTER_DEVICE_PLACEHOLDER);
    setIsDeviceQueryApplied(true);
    setSelectedEvents([]);
    setStartDate(resetRange.startDate);
    setEndDate(resetRange.endDate);
    setOpenMenu(null);
    setPage(1);
  };

  const handleDeviceSelection = (value) => {
    setSelectedDevice(value);
    if (requestedDeviceQuery) {
      setIsDeviceQueryApplied(true);
    }
    setOpenMenu(null);
  };

  const handleVehicleListSelection = (vehicle) => {
    const nextDeviceId = vehicle?.uniqueId || EVENT_CENTER_DEVICE_PLACEHOLDER;
    setSelectedDevice(nextDeviceId);
    if (requestedDeviceQuery) {
      setIsDeviceQueryApplied(true);
    }
    setOpenMenu(null);
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)] bg-[#edf1f5] p-1 sm:p-4" style={{ zoom: 0.8 }}>
      <div className={layoutGridClass}>
        {isEventCenterV2Route ? (
          <div className="min-h-[760px]">
            <VehicleList
              vehicles={devices}
              selectedVehicle={selectedVehicleEntry}
              selectedVehicleIds={
                selectedDevice && selectedDevice !== EVENT_CENTER_DEVICE_PLACEHOLDER
                  ? [selectedDevice]
                  : []
              }
              onSelectedVehicleChange={handleVehicleListSelection}
              designVariant="groups-panel"
              showVehicleActions={false}
            />
          </div>
        ) : (
          <aside className="flex min-h-[760px] flex-col overflow-hidden rounded border border-[#dfe5ec] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between border-b border-[#edf1f5] px-4 py-3">
              <div className="flex items-center gap-3 text-[#1f1f1f]">
                <FiUsers className="h-4 w-4 text-[#1677ff]" />
                <span className="text-[16px] font-medium">My Client</span>
              </div>
              <span className="grid h-7 w-7 place-items-center rounded border border-[#d9dfe7] text-[#98a2b3]">
                <FiMenu className="h-4 w-4" />
              </span>
            </div>

            <div className="border-b border-[#edf1f5] p-3">
              <div className="flex items-center gap-2 border border-[#d9dfe7] bg-[#f8fafc] px-3 py-2 text-[13px] text-[#98a2b3]">
                <FiSearch className="h-4 w-4 shrink-0" />
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(event) => setUserSearchTerm(event.target.value)}
                  placeholder="Account/Name"
                  className="h-full w-full bg-transparent text-[13px] text-[#4c5f78] outline-none placeholder:text-[#98a2b3]"
                />
              </div>
            </div>

            <div className="flex-1 bg-white">
              {myUserEntries.length === 0 ? (
                <div className="px-4 py-6 text-[13px] text-[#98a2b3]">No user found</div>
              ) : (
                myUserEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between border-b border-[#edf1f5] bg-[#edf4fb] px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-[#f2ae1d]" />
                      <span className="text-[13px] font-semibold text-[#1677ff]">{entry.label}</span>
                    </div>
                    <FaEllipsisV className="h-3 w-3 text-[#6c7b8a]" />
                  </div>
                ))
              )}
            </div>
          </aside>
        )}

        <div className="rounded border border-[#dfe5ec] bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          <div className="border-b border-[#edf1f5] pb-2 text-[16px] font-semibold text-[#1f1f1f]">{t("eventCenter.title")}</div>

          <div className="relative z-20 mt-3 flex flex-wrap items-start gap-4">
            {!isEventCenterV2Route ? (
              <div onPointerDown={(event) => event.stopPropagation()}>
                <EventCenterSelect
                  label={t("eventCenter.device")}
                  value={selectedDevice}
                  options={deviceOptions}
                  isOpen={openMenu === "device"}
                  onToggle={() => setOpenMenu((prev) => (prev === "device" ? null : "device"))}
                  onSelect={handleDeviceSelection}
                />
              </div>
            ) : null}

            <div onPointerDown={(event) => event.stopPropagation()}>
              <EventCenterMultiSelect
                label={t("eventCenter.event")}
                values={selectedEvents}
                options={eventOptions}
                isOpen={openMenu === "event"}
                onToggle={() => setOpenMenu((prev) => (prev === "event" ? null : "event"))}
                onChange={setSelectedEvents}
                isLoading={alertTypesLoading}
              />
            </div>

            <div onPointerDown={(event) => event.stopPropagation()}>
              <EventCenterDatePicker
                isOpen={openMenu === "date"}
                onToggle={() => setOpenMenu((prev) => (prev === "date" ? null : "date"))}
                onClose={() => setOpenMenu(null)}
                startDate={startDate}
                endDate={endDate}
                onChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                  setStartDate(nextStartDate);
                  setEndDate(nextEndDate);
                }}
              />
            </div>

            <div className="ml-auto flex items-center gap-3 pt-[1px]">
              <button
                type="button"
                onClick={handleSearch}
                className="flex h-[32px] items-center gap-2 rounded bg-[#10b981] px-4 text-[14px] text-white"
              >
                <FiSearch className="h-4 w-4" />
                {t("eventCenter.search")}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="h-[32px] rounded border border-[#d9dfe7] bg-white px-4 text-[14px] text-[#667085]"
              >
                {t("eventCenter.reset")}
              </button>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto border-t border-[#eef2f7] pt-3">
            {loading ? (
              <div className="mb-3 flex items-center justify-center rounded border border-[#dbe4ee] bg-[#f8fbff] px-4 py-3 text-[13px] text-[#4c5f78]">
                {t("eventCenter.loading_events")}
              </div>
            ) : null}
            {error ? (
              <div className="mb-3 rounded border border-[#ffd5d5] bg-[#fff6f6] px-4 py-3 text-[13px] text-[#c45d68]">
                {t("eventCenter.loading_error")}
              </div>
            ) : null}
            <table className="min-w-full text-left text-[12px] text-[#444]">
              <thead className="bg-[#edf4fb] text-[12px] font-semibold text-[#333]">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">{t("eventCenter.operate")}</th>
                  <th className="px-4 py-2">{t("eventCenter.attachments")}</th>
                  <th className="px-4 py-2">{t("eventCenter.event_name")}</th>
                  <th className="px-4 py-2">{t("eventCenter.plate_number")}</th>
                  <th className="px-4 py-2">{t("eventCenter.driver")}</th>
                  <th className="px-4 py-2">{t("eventCenter.belong_to")}</th>
                  <th className="px-4 py-2">{t("eventCenter.speed")}</th>
                  <th className="px-4 py-2">{t("eventCenter.time")}</th>
                  <th className="px-4 py-2 text-[#c45d68]">{t("eventCenter.address")}</th>
                </tr>
              </thead>
              <tbody>
                {!loading && paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-[13px] text-[#7b8797]">
                      {t("eventCenter.no_event_found")}
                    </td>
                  </tr>
                ) : null}
                {paginatedRows.map((row) => (
                  <tr key={row.id} className="border-b border-[#edf1f5]">
                    <td className="px-4 py-1.5">{row.id}</td>
                    <td className="px-4 py-1.5">
                      <div className="flex items-center gap-2 text-[#10b981]">
                        <button
                          type="button"
                          onClick={() => setOperateModal({ type: "preview", row })}
                          className="rounded p-0.5 hover:bg-[#eef6ff]"
                          title={t("eventCenter.preview_event")}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setOperateModal({ type: "track", row })}
                          className="rounded p-0.5 hover:bg-[#eef6ff]"
                          title={t("eventCenter.track_playback")}
                        >
                          <Send className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-1.5">{row.attachments}</td>
                    <td className="px-4 py-1.5">{row.eventName}</td>
                    <td className="px-4 py-1.5">{row.plateNumber}</td>
                    <td className="px-4 py-1.5">{row.driver}</td>
                    <td className="px-4 py-1.5">{row.belongTo}</td>
                    <td className="px-4 py-1.5">{row.speed}</td>
                    <td className="px-4 py-1.5">{row.time}</td>
                    <td className="px-4 py-1.5 text-[#666]">{row.address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#edf1f5] pt-3 text-[12px] text-[#5f6b7a]">
            <div>{t("eventCenter.total", { total: rows.length })}</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="grid h-6 w-6 place-items-center rounded border border-[#e1e6ee] bg-white text-[#9aa7b6] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {"<"}
              </button>
              {visiblePages.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setPage(pageNumber)}
                  className={`grid h-6 min-w-6 place-items-center rounded px-2 ${pageNumber === page ? "bg-[#10b981] text-white" : "bg-[#f2f5f9] text-[#5f6b7a]"}`}
                >
                  {pageNumber}
                </button>
              ))}
              <span className="ml-2">
                {t("eventCenter.page", { current: page, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="grid h-6 w-6 place-items-center rounded border border-[#e1e6ee] bg-white text-[#5f6b7a] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {">"}
              </button>
              <div className="ml-3 grid h-6 min-w-[100px] place-items-center rounded border border-[#d9dfe7] bg-white px-3">
                {t("eventCenter.page_size", { size: pageSize })}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isAutoOpenPending ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/65 backdrop-blur-[1px]">
          <div className="flex min-w-[220px] flex-col items-center gap-3 rounded-xl border border-[#dfe5ec] bg-white px-6 py-5 shadow-[0_12px_30px_rgba(15,23,42,0.12)]">
            <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#d9f3ea] border-t-[#10b981]" />
            <div className="text-center">
              <div className="text-[14px] font-medium text-[#1f2937]">Ouverture de l&apos;evenement...</div>
              <div className="mt-1 text-[12px] text-[#6b7280]">Chargement des donnees en cours</div>
            </div>
          </div>
        </div>
      ) : null}
      <EventCenterOperateModal modalState={operateModal} onClose={() => setOperateModal(null)} />
    </div>
  );
}





// Vitesse maximale par zone (pour affichage dans une card unique)





const INCIDENT_SERIES = [
  { key: "hardAcceleration", label: "Accélération brusque", color: "#f97316" },
  { key: "overspeed", label: "Excès de vitesse", color: "#22c55e" },
  { key: "hardBraking", label: "Freinage brusque", color: "#eab308" },
  { key: "panicButton", label: "Panic bouton", color: "#ef4444" },
  { key: "speedLimitViolation", label: "Violation limite vitesse", color: "#3b82f6" },
];










const alertItems = [
  { id: 1, color: "#ef4444", label: "Niveau critique", arrivedAt: "08:12", status: "Urgent", vehicle: "Véhicule CE-002" },
  { id: 2, color: "#f97316", label: "Congestion du trafic", arrivedAt: "08:27", status: "Moyen", vehicle: "Véhicule LT-004" },
  { id: 3, color: "#eab308", label: "Péage autoroutier", arrivedAt: "08:41", status: "Normal", vehicle: "Véhicule OU-003" },
  { id: 4, color: "#fb7185", label: "Priorité élevée", arrivedAt: "09:33", status: "Urgent", vehicle: "Véhicule CE-001" },
  { id: 5, color: "#22c55e", label: "Signalement social", arrivedAt: "10:05", status: "Normal", vehicle: "Véhicule LT-001" },
  { id: 6, color: "#ef4444", label: "Alerte collision", arrivedAt: "10:14", status: "Urgent", vehicle: "Véhicule CE-004" },
  { id: 7, color: "#f97316", label: "Bouchon axe Nord", arrivedAt: "10:22", status: "Moyen", vehicle: "Véhicule LT-006" },
  { id: 8, color: "#eab308", label: "Ralentissement péage", arrivedAt: "10:37", status: "Normal", vehicle: "Véhicule OU-006" },
  { id: 9, color: "#fb7185", label: "Arrêt non autorisé", arrivedAt: "10:45", status: "Moyen", vehicle: "Véhicule CE-007" },
  { id: 10, color: "#22c55e", label: "Signalement usager", arrivedAt: "10:58", status: "Info", vehicle: "Véhicule LT-008" },
  { id: 11, color: "#ef4444", label: "Incident majeur", arrivedAt: "11:06", status: "Urgent", vehicle: "Véhicule CE-010" },
  { id: 12, color: "#f97316", label: "Déviation saturée", arrivedAt: "11:20", status: "Moyen", vehicle: "Véhicule OU-009" },
  { id: 13, color: "#eab308", label: "Alerte signalisation", arrivedAt: "11:33", status: "Normal", vehicle: "Véhicule LT-011" },
  { id: 14, color: "#fb7185", label: "Risque de blocage", arrivedAt: "11:47", status: "Urgent", vehicle: "Véhicule CE-012" },
];

const commentItems = [
  { id: 101, color: "#10b981", label: "Le trafic est fluide ce matin.", arrivedAt: "08:49", author: "Usager A" },
  { id: 102, color: "#38bdf8", label: "Ralentissement au rond-point central.", arrivedAt: "09:02", author: "Usager B" },
  { id: 103, color: "#f59e0b", label: "Signalisation peu visible à l'entrée Est.", arrivedAt: "09:15", author: "Usager C" },
  { id: 104, color: "#a78bfa", label: "Déviation bien indiquée mais trop longue.", arrivedAt: "09:48", author: "Usager D" },
];

const incidentRows = [
  {
    vehicle: "Véhicule CE-002",
    incidentDate: "2026-02-19 08:12",
    entryDate: "2026-02-19 08:12",
    exitDate: "2026-02-19 08:32",
    location: "Carrefour Logpom",
    type: "Panic bouton",
  },
  {
    vehicle: "Véhicule LT-004",
    incidentDate: "2026-02-19 08:48",
    entryDate: "2026-02-19 08:48",
    exitDate: "2026-02-19 09:10",
    location: "Boulevard Deido",
    type: "Accélération brusque",
  },
  {
    vehicle: "Véhicule OU-003",
    incidentDate: "2026-02-19 09:22",
    entryDate: "2026-02-19 09:22",
    exitDate: "2026-02-19 09:44",
    location: "Rond-point Bonamoussadi",
    type: "Freinage brusque",
  },
  {
    vehicle: "Véhicule CE-001",
    incidentDate: "2026-02-19 09:55",
    entryDate: "2026-02-19 09:55",
    exitDate: "2026-02-19 10:21",
    location: "Axe Douala V",
    type: "Excès de vitesse",
  },
  {
    vehicle: "Véhicule LT-001",
    incidentDate: "2026-02-19 10:35",
    entryDate: "2026-02-19 10:35",
    exitDate: "2026-02-19 11:02",
    location: "Entrée Zone École",
    type: "Violation limite vitesse",
  },
];

// Mock trip data for replay modal


const INCIDENTS_PAGE_SIZE = 4;







// Mapping des véhicules - à remplacer par des données réelles ou une API
const VEHICLE_MAPPING = {
  "Véhicule CE-002": { uniqueId: "it_864714067581190", vehicleNumber: "BUS 1" },
  "Véhicule LT-004": { uniqueId: "it_863406070712026", vehicleNumber: "BUS 2" },
  "Véhicule OU-003": { uniqueId: "it_863406070676817", vehicleNumber: "BUS 3" },
  "Véhicule CE-001": { uniqueId: "it_864714067575200", vehicleNumber: "BUS 4" },
  "Véhicule LT-001": { uniqueId: "it_863406070710988", vehicleNumber: "BUS 5" },
};

function MonitorConsole({ isTrackingExpanded, onExpand, onClose, schools = [], schoolsLoading = false }) {
  const [isMapTypeMenuOpen, setIsMapTypeMenuOpen] = useState(false);
  const [isMoreFiltersMenuOpen, setIsMoreFiltersMenuOpen] = useState(false);
  const [schoolPage, setSchoolPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [listMode, setListMode] = useState("bus");
  const [listPage, setListPage] = useState(0);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState({});
  const [selectedTripIds, setSelectedTripIds] = useState({});
  const [selectedPickupIds, setSelectedPickupIds] = useState({});
  const SCHOOLS_PER_PAGE = 3;
  const ITEMS_PER_PAGE = 5;
  const clientLoginId = getLocalStorage("clientLoginId") || 1172;
  const { devices = [] } = useDevices();
  const { pickupPoints = [] } = usePickupPoints(clientLoginId);
  const { tripData: routeTrips = [], routesTrips = [] } = useTravelHistory(clientLoginId);
  const monitorTrips = Array.isArray(routesTrips) && routesTrips.length > 0 ? routesTrips : routeTrips;

  const triggerMapControl = (selector) => {
    const control = document.querySelector(selector);
    if (control) {
      control.click();
    }
  };

  const handleMapTypeChange = (mapTypeId) => {
    window.dispatchEvent(
      new CustomEvent("lewoo:set-map-type", {
        detail: {
          scope: "monitor",
          mapTypeId,
        },
      })
    );
    setIsMapTypeMenuOpen(false);
  };

  // Centrer la carte sur une école
  const focusOnSchool = (school) => {
    if (school?.latitude && school?.longitude) {
      window.dispatchEvent(
        new CustomEvent("lewoo:center-map", {
          detail: {
            scope: "monitor",
            lat: parseFloat(school.latitude),
            lng: parseFloat(school.longitude),
            zoom: 15,
          },
        })
      );
    }
  };

  // Pagination des écoles
  const totalPages = Math.ceil(schools.length / SCHOOLS_PER_PAGE);
  const paginatedSchools = schools.slice(
    schoolPage * SCHOOLS_PER_PAGE,
    (schoolPage + 1) * SCHOOLS_PER_PAGE
  );

  const handlePrevPage = () => {
    setSchoolPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setSchoolPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const focusOnCoordinates = (lat, lng, zoom = 15) => {
    if (!lat || !lng) return;
    window.dispatchEvent(
      new CustomEvent("lewoo:center-map", {
        detail: {
          scope: "monitor",
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          zoom,
        },
      })
    );
  };

  const focusOnDevice = (device) => {
    focusOnCoordinates(device?.latitude, device?.longitude, 16);
  };

  const focusOnPickupPoint = (point) => {
    focusOnCoordinates(point?.latitude, point?.longitude, 16);
  };

  const getTripKey = (trip) => trip?.routeId || trip?.tripId || trip?.id;

  const getTripLabel = (trip, index = 0) =>
    trip?.routeName || trip?.tripName || trip?.name || `Itineraire ${index + 1}`;

  const focusOnTrip = (trip) => {
    const firstPoint = trip?.path?.[0];
    if (firstPoint?.lat && firstPoint?.lng) {
      focusOnCoordinates(firstPoint.lat, firstPoint.lng, 14);
      return;
    }

    const fallbackPoint =
      (trip?.startPoint?.lat && trip?.startPoint?.lng && trip.startPoint) ||
      (trip?.startLocation?.latitude && trip?.startLocation?.longitude
        ? { lat: trip.startLocation.latitude, lng: trip.startLocation.longitude }
        : null) ||
      (trip?.stops?.[0]?.stop?.latitude && trip?.stops?.[0]?.stop?.longitude
        ? { lat: trip.stops[0].stop.latitude, lng: trip.stops[0].stop.longitude }
        : null) ||
      (trip?.endLocation?.latitude && trip?.endLocation?.longitude
        ? { lat: trip.endLocation.latitude, lng: trip.endLocation.longitude }
        : null);

    if (fallbackPoint?.lat && fallbackPoint?.lng) {
      focusOnCoordinates(fallbackPoint.lat, fallbackPoint.lng, 14);
    }
  };

  const normalizeQuery = searchTerm.trim().toLowerCase();

  const filteredDevices = useMemo(
    () =>
      devices.filter((device) => {
        if (!normalizeQuery) return true;
        return [device?.vehicleNumber, device?.uniqueId, device?.imei_num]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizeQuery));
      }),
    [devices, normalizeQuery]
  );

  const filteredPickupPoints = useMemo(
    () =>
      pickupPoints.filter((point) => {
        if (!normalizeQuery) return true;
        return String(point?.name || "").toLowerCase().includes(normalizeQuery);
      }),
    [pickupPoints, normalizeQuery]
  );

  const filteredTrips = useMemo(
    () =>
      (Array.isArray(monitorTrips) ? monitorTrips : []).filter((trip) => {
        if (!normalizeQuery) return true;
        return String(getTripLabel(trip))
          .toLowerCase()
          .includes(normalizeQuery);
      }),
    [monitorTrips, normalizeQuery]
  );

  const getDeviceMeta = (device) => {
    if (device?.isOffline) return { label: "Offline", tone: "text-[#94a0b2]" };
    if (device?.isNoGps) return { label: "No GPS", tone: "text-[#d39c2a]" };
    if (device?.haltStatus) return { label: "Arret", tone: "text-[#ef4444]" };
    if (device?.idlingStatus) return { label: "Idle", tone: "text-[#f59e0b]" };
    return { label: "En ligne", tone: "text-[#10b981]" };
  };

  useEffect(() => {
    setSelectedDeviceIds((prev) => {
      const next = {};
      devices.forEach((device) => {
        const key = device?.uniqueId;
        if (key) next[key] = prev[key] ?? true;
      });
      return next;
    });
  }, [devices]);

  useEffect(() => {
    setSelectedTripIds((prev) => {
      const next = {};
      (Array.isArray(monitorTrips) ? monitorTrips : []).forEach((trip) => {
        const key = getTripKey(trip);
        if (key) next[key] = prev[key] ?? true;
      });
      return next;
    });
  }, [monitorTrips]);

  useEffect(() => {
    setSelectedPickupIds((prev) => {
      const next = {};
      pickupPoints.forEach((point) => {
        const key = point?.id;
        if (key) next[key] = prev[key] ?? true;
      });
      return next;
    });
  }, [pickupPoints]);

  useEffect(() => {
    devices.forEach((device) => {
      if (!device?.uniqueId) return;
      window.dispatchEvent(
        new CustomEvent("lewoo:toggle-visibility", {
          detail: {
            scope: "monitor",
            itemId: device.uniqueId,
            itemType: "bus",
            isVisible: selectedDeviceIds[device.uniqueId] ?? true,
          },
        })
      );
    });
  }, [devices, selectedDeviceIds]);

  useEffect(() => {
    (Array.isArray(monitorTrips) ? monitorTrips : []).forEach((trip) => {
      const key = getTripKey(trip);
      if (!key) return;
      window.dispatchEvent(
        new CustomEvent("lewoo:toggle-visibility", {
          detail: {
            scope: "monitor",
            itemId: key,
            itemType: "itineraire",
            isVisible: selectedTripIds[key] ?? true,
          },
        })
      );
    });
  }, [monitorTrips, selectedTripIds]);

  useEffect(() => {
    pickupPoints.forEach((point) => {
      if (!point?.id) return;
      window.dispatchEvent(
        new CustomEvent("lewoo:toggle-visibility", {
          detail: {
            scope: "monitor",
            itemId: point.id,
            itemType: "pickup",
            isVisible: selectedPickupIds[point.id] ?? true,
          },
        })
      );
    });
  }, [pickupPoints, selectedPickupIds]);

  useEffect(() => {
    setListPage(0);
  }, [listMode, searchTerm]);

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDeviceIds((prev) => ({
      ...prev,
      [deviceId]: !(prev[deviceId] ?? true),
    }));
  };

  const toggleTripSelection = (tripId) => {
    setSelectedTripIds((prev) => ({
      ...prev,
      [tripId]: !(prev[tripId] ?? true),
    }));
  };

  const togglePickupSelection = (pickupId) => {
    setSelectedPickupIds((prev) => ({
      ...prev,
      [pickupId]: !(prev[pickupId] ?? true),
    }));
  };

  const setSelectionForMode = (mode, selected) => {
    if (mode === "bus") {
      setSelectedDeviceIds(
        filteredDevices.reduce((acc, device) => {
          if (device?.uniqueId) acc[device.uniqueId] = selected;
          return acc;
        }, {})
      );
      return;
    }

    if (mode === "trip") {
      setSelectedTripIds(
        filteredTrips.reduce((acc, trip) => {
          const key = getTripKey(trip);
          if (key) acc[key] = selected;
          return acc;
        }, {})
      );
      return;
    }

    setSelectedPickupIds(
      filteredPickupPoints.reduce((acc, point) => {
        if (point?.id) acc[point.id] = selected;
        return acc;
      }, {})
    );
  };

  const getSelectedCountForMode = (mode) => {
    if (mode === "bus") {
      return filteredDevices.filter((device) => selectedDeviceIds[device.uniqueId] ?? true).length;
    }
    if (mode === "trip") {
      return filteredTrips.filter((trip) => selectedTripIds[getTripKey(trip)] ?? true).length;
    }
    return filteredPickupPoints.filter((point) => selectedPickupIds[point.id] ?? true).length;
  };

  const currentItems =
    listMode === "bus"
      ? filteredDevices
      : listMode === "trip"
        ? filteredTrips
        : filteredPickupPoints;
  const selectedCountForMode = getSelectedCountForMode(listMode);
  const allSelectedForMode = currentItems.length > 0 && selectedCountForMode === currentItems.length;
  const totalListPages = Math.max(1, Math.ceil(currentItems.length / ITEMS_PER_PAGE));
  const paginatedItems = currentItems.slice(
    listPage * ITEMS_PER_PAGE,
    (listPage + 1) * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (listPage > totalListPages - 1) {
      setListPage(Math.max(0, totalListPages - 1));
    }
  }, [listPage, totalListPages]);


  return (
    <div className="bg-[#ebedf0] rounded-xl">
      <div className="monitor-map-stage relative h-[calc(100vh-140px)] min-h-[520px] overflow-hidden bg-white">
        <style>
          {`
            .monitor-map-stage .gm-style-mtc {
              opacity: 0 !important;
              pointer-events: none !important;
            }

            .monitor-map-stage .gm-fullscreen-control {
              opacity: 0 !important;
              pointer-events: none !important;
            }

            .monitor-map-stage #traffic-layer-control {
              opacity: 0 !important;
              pointer-events: none !important;
            }

            .monitor-map-stage .gm-bundled-control {
              opacity: 0 !important;
              pointer-events: none !important;
            }
          `}
        </style>
        <div className="absolute inset-0">
          <TrackingPageLite2
            isExpanded={isTrackingExpanded}
            onExpand={onExpand}
            onClose={onClose}
            embeddedMode
            mapControlScope="monitor"
          />
        </div>

        <div className="pointer-events-none absolute inset-0">
          <div className="pointer-events-auto absolute right-[20px] top-[72px] z-20 flex flex-col items-center gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsMapTypeMenuOpen((prev) => !prev)}
                className="flex h-8 w-8 items-center justify-center bg-white text-[#5d6778] shadow-[0_1px_4px_rgba(0,0,0,0.25)]"
                title="Type de carte"
              >
                <FaSatellite className="h-4 w-4" />
              </button>

              {isMapTypeMenuOpen ? (
                <div className="absolute right-10 top-0 w-[120px] overflow-hidden bg-white text-[11px] text-[#334155] shadow-[0_4px_16px_rgba(0,0,0,0.18)]">
                  <button
                    type="button"
                    onClick={() => handleMapTypeChange("satellite")}
                    className="block w-full px-3 py-2 text-left hover:bg-[#f3f4f6]"
                  >
                    Mode satellite
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMapTypeChange("roadmap")}
                    className="block w-full border-t border-[#e5e7eb] px-3 py-2 text-left hover:bg-[#f3f4f6]"
                  >
                    Mode normal
                  </button>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => triggerMapControl(".monitor-map-stage .gm-fullscreen-control")}
              className="flex h-8 w-8 items-center justify-center bg-white text-[#5d6778] shadow-[0_1px_4px_rgba(0,0,0,0.25)]"
              title="Plein ecran"
            >
              <Maximize2 className="h-4 w-4" />
            </button>

            <div className="overflow-hidden bg-white shadow-[0_1px_4px_rgba(0,0,0,0.25)]">
              <button
                type="button"
                onClick={() => triggerMapControl('.monitor-map-stage button[aria-label="Zoom in"]')}
                className="flex h-8 w-8 items-center justify-center border-b border-[#e5e7eb] text-[#5d6778]"
                title="Zoom avant"
              >
                <FiPlus className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => triggerMapControl('.monitor-map-stage button[aria-label="Zoom out"]')}
                className="flex h-8 w-8 items-center justify-center text-[#5d6778]"
                title="Zoom arriere"
              >
                <FiMinus className="h-4 w-4" />
              </button>
            </div>

            <button
              type="button"
              onClick={() => triggerMapControl("#traffic-layer-control")}
              className="flex h-8 w-8 items-center justify-center bg-white text-[#5d6778] shadow-[0_1px_4px_rgba(0,0,0,0.25)]"
              title="Traffic"
            >
              <FiGrid className="h-4 w-4" />
            </button>
          </div>

          <div className="pointer-events-auto absolute left-5 top-5 bottom-5 z-20 w-[235px] bg-[#f5f6f8] shadow-[0_1px_3px_rgba(0,0,0,0.12)]">
            <div className="border-b border-[#d7dee8] bg-[#fbfbfc] px-3 py-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[1.02rem] font-medium text-[#202a36]">
                  <FiGrid className="h-4 w-4 text-[#10b981]" />
                  <span>Mes ecoles</span>
                </div>
                <button className="rounded-sm border border-[#d2dae5] bg-white p-1.5 text-[#607086]">
                  <FiMenu className="h-4 w-4" />
                </button>
              </div>
              <div className="mb-3 border-b border-[#10b981] pb-2 text-sm font-medium text-[#10b981]">Default</div>
              <div className="flex overflow-hidden border border-[#d5dde8] bg-white">
                <div className="flex h-[34px] flex-1 items-center gap-2 px-3">
                  <FiSearch className="h-4 w-4 text-[#96a3b4]" />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Mes ecoles"
                    className="h-full w-full bg-transparent text-[11px] text-[#4c5f78] outline-none placeholder:text-[#96a3b4]"
                  />
                </div>

              </div>
            </div>

            <div className="border-b border-[#d7dee8] bg-[#edf3fb] px-4 py-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-[#10b981]">
                  <School className="h-4 w-4 text-[#f0ac1c]" />
                  <span>Ecoles ({schools.length})</span>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handlePrevPage}
                      disabled={schoolPage === 0}
                      className="rounded border border-[#cfd7e2] bg-white p-1 text-[#546476] disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <span className="text-[11px] text-[#546476]">
                      {schoolPage + 1} / {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={schoolPage === totalPages - 1}
                      className="rounded border border-[#cfd7e2] bg-white p-1 text-[#546476] disabled:opacity-40"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {schoolsLoading ? (
                  <div className="py-3 text-center text-[11px] text-[#94a0b2]">Chargement...</div>
                ) : paginatedSchools.length === 0 ? (
                  <div className="py-3 text-center text-[11px] text-[#94a0b2]">Aucune école trouvée</div>
                ) : (
                  paginatedSchools.map((school) => (
                    <button
                      key={school.id}
                      onClick={() => focusOnSchool(school)}
                      className="flex w-full items-center justify-between rounded px-2 py-2 text-[11px] hover:bg-[#dbe8f6]"
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-[#27ae60]" />
                        <span className="font-medium text-[#1d2f46]">{school.name}</span>
                      </div>
                      <MapPin className="h-3.5 w-3.5 text-[#10b981]" />
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-[#d7dee8] bg-white px-4 py-3 text-[11px] text-[#1d2f46]">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setListMode("bus")}
                  className={listMode === "bus" ? "font-semibold text-[#10b981]" : ""}
                >
                  Bus({devices.length})
                </button>
                <button
                  type="button"
                  onClick={() => setListMode("trip")}
                  className={listMode === "trip" ? "font-semibold text-[#10b981]" : ""}
                >
                  Itineraire({monitorTrips.length})
                </button>
                <button
                  type="button"
                  onClick={() => setListMode("pickup")}
                  className={listMode === "pickup" ? "font-semibold text-[#10b981]" : ""}
                >
                  Stop({pickupPoints.length})
                </button>
              </div>
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center justify-center rounded p-1 text-[#4c5f78] hover:bg-[#edf3fb]"
                  onClick={() => setIsMoreFiltersMenuOpen((prev) => !prev)}
                  aria-label="Plus de filtres"
                >
                  <FaEllipsisV className="h-4 w-4" />
                </button>
                {isMoreFiltersMenuOpen && (
                  <div className="absolute right-0 top-7 w-40 overflow-hidden rounded border border-[#e2e8f0] bg-white text-[11px] text-[#1d2f46] shadow-[0_4px_12px_rgba(15,23,42,0.12)]">
                    <button
                      type="button"
                      className="block w-full px-3 py-2 text-left hover:bg-[#f1f5f9]"
                      onClick={() => setIsMoreFiltersMenuOpen(false)}
                    >
                      Limitation vitesse
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="border-b border-[#d7dee8] bg-white px-3 py-3">
              <div className="mb-2 flex items-center justify-between bg-[#eef1f5] px-3 py-2 text-[11px] font-medium text-[#5e6d80]">
                <span>
                  {listMode === "bus"
                    ? `Default Group(${filteredDevices.length})`
                    : listMode === "trip"
                      ? `Itineraires(${filteredTrips.length})`
                      : `Ramassage(${filteredPickupPoints.length})`}
                </span>
                <FiChevronDown className="h-4 w-4" />
              </div>
              <div className="mb-2 flex items-center justify-between rounded border border-[#d7dee8] bg-[#f8fafc] px-3 py-2 text-[10px] text-[#5e6d80]">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allSelectedForMode}
                    onChange={(event) => setSelectionForMode(listMode, event.target.checked)}
                    className="h-3.5 w-3.5 cursor-pointer accent-[#10b981]"
                  />
                  <span className="font-medium text-[#4c5f78]">Tout selectionner</span>
                </label>
                <button
                  type="button"
                  onClick={() => setSelectionForMode(listMode, false)}
                  className="text-[10px] font-medium text-[#7b8797] hover:text-[#4c5f78]"
                >
                  Tout deselectionner
                </button>
              </div>
              <div className="space-y-0.5">
                {listMode === "bus" &&
                  paginatedItems.map((vehicle, index) => {
                    const meta = getDeviceMeta(vehicle);
                    const isSelected = selectedDeviceIds[vehicle.uniqueId] ?? true;
                    return (
                      <div
                        key={vehicle.uniqueId || vehicle.vehicleNumber || index}
                        onClick={() => focusOnDevice(vehicle)}
                        className={`grid w-full grid-cols-[12px_minmax(0,1fr)_auto_18px] items-center gap-2 px-3 py-2 text-[11px] ${isSelected ? "bg-[#dbe8f6]" : "bg-transparent hover:bg-[#edf3fa]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDeviceSelection(vehicle.uniqueId)}
                          onClick={(event) => event.stopPropagation()}
                          className="h-3 w-3 cursor-pointer accent-[#10b981]"
                        />
                        <span className={isSelected ? "truncate font-semibold text-[#10b981]" : "truncate text-[#536579]"}>
                          {vehicle.vehicleNumber || vehicle.uniqueId}
                        </span>
                        <span className={`text-xs font-medium ${meta.tone}`}>{meta.label}</span>
                        <FiMoreHorizontal className="h-4 w-4 text-[#69788c]" />
                      </div>
                    );
                  })}
                {listMode === "trip" &&
                  paginatedItems.map((trip, index) => {
                    const tripKey = getTripKey(trip);
                    const isSelected = selectedTripIds[tripKey] ?? true;
                    return (
                      <div
                        key={tripKey || index}
                        onClick={() => focusOnTrip(trip)}
                        className={`grid w-full grid-cols-[12px_minmax(0,1fr)_auto_18px] items-center gap-2 px-3 py-2 text-[11px] ${isSelected ? "bg-[#dbe8f6]" : "bg-transparent hover:bg-[#edf3fa]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTripSelection(tripKey)}
                          onClick={(event) => event.stopPropagation()}
                          className="h-3 w-3 cursor-pointer accent-[#10b981]"
                        />
                        <span className={isSelected ? "truncate font-semibold text-[#10b981]" : "truncate text-[#536579]"}>
                          {getTripLabel(trip, index)}
                        </span>
                        <span className="text-xs font-medium text-[#10b981]">{trip.distanceKm || 0} km</span>
                        <FiMoreHorizontal className="h-4 w-4 text-[#69788c]" />
                      </div>
                    );
                  })}
                {listMode === "pickup" &&
                  paginatedItems.map((point, index) => {
                    const isSelected = selectedPickupIds[point.id] ?? true;
                    return (
                      <div
                        key={point.id || index}
                        onClick={() => focusOnPickupPoint(point)}
                        className={`grid w-full grid-cols-[12px_minmax(0,1fr)_auto_18px] items-center gap-2 px-3 py-2 text-[11px] ${isSelected ? "bg-[#dbe8f6]" : "bg-transparent hover:bg-[#edf3fa]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => togglePickupSelection(point.id)}
                          onClick={(event) => event.stopPropagation()}
                          className="h-3 w-3 cursor-pointer accent-[#10b981]"
                        />
                        <span className={isSelected ? "truncate font-semibold text-[#10b981]" : "truncate text-[#536579]"}>
                          {point.name || `Ramassage ${index + 1}`}
                        </span>
                        <span className="text-xs font-medium text-[#28a467]">Point</span>
                        <FiMoreHorizontal className="h-4 w-4 text-[#69788c]" />
                      </div>
                    );
                  })}
                {((listMode === "bus" && filteredDevices.length === 0) ||
                  (listMode === "trip" && filteredTrips.length === 0) ||
                  (listMode === "pickup" && filteredPickupPoints.length === 0)) && (
                    <div className="px-3 py-6 text-center text-[11px] text-[#94a0b2]">
                      Aucun element trouve
                    </div>
                  )}
              </div>
            </div>

            <div className="bg-[#f5f6f8] px-3 py-2">
              <div className="mb-2 flex items-center justify-between gap-2 text-[#546476]">
                <button
                  type="button"
                  onClick={() => setListPage((prev) => Math.max(0, prev - 1))}
                  disabled={listPage === 0}
                  className="rounded border border-[#d8dde6] bg-white px-3 py-2 text-[11px] font-medium text-[#7d8794] disabled:bg-[#f7f7f8] disabled:text-[#c0c6ce]"
                >
                  Precedent
                </button>
                <div className="rounded border border-[#d8dde6] bg-white px-3 py-2 text-[11px] font-medium text-[#5f6b7a]">
                  Page {listPage + 1} / {totalListPages}
                </div>
                <button
                  type="button"
                  onClick={() => setListPage((prev) => Math.min(totalListPages - 1, prev + 1))}
                  disabled={listPage >= totalListPages - 1}
                  className="rounded border border-[#d8dde6] bg-white px-3 py-2 text-[11px] font-medium text-[#5f6b7a] disabled:bg-[#f7f7f8] disabled:text-[#c0c6ce]"
                >
                  Suivant
                </button>
              </div>
              <div className="flex items-center justify-center text-[10px] font-medium text-[#7b8797]">
                {selectedCountForMode} selectionne(s) sur {currentItems.length}
              </div>
            </div>
          </div>

          <div className="pointer-events-auto absolute left-[275px] right-5 top-5 z-20 flex h-[42px] items-center justify-between bg-white px-3 text-[11px] shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
            <div className="flex h-full items-center gap-5">
              <button className="flex h-full items-center gap-2 border-b-2 border-[#10b981] font-medium text-[#10b981]">
                <FiMapPin className="h-4 w-4" />
                Vue d'ensemble
              </button>
              <button className="flex items-center gap-2 text-[#1f2937]">
                <Film className="h-4 w-4" />
                Suivi de flotte
              </button>
              <span className="bg-[#ebebeb] px-3 py-1 text-[#4c5f78]">Cameroon(2026-03-11 22:05:47)</span>
            </div>
            <div className="border border-[#d7dee8] bg-[#f8fbff] px-3 py-1 text-[#10b981]">Refresh in 6 seconds.Refresh</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const EventCenter = forwardRef((_, ref) => {
  const mapOverviewRef = useRef(null);
  const outletContext = useOutletContext() || {};
  const { allAreas = [] } = outletContext;
  const [commentInput, setCommentInput] = useState("");
  const [incidentPage, setIncidentPage] = useState(1);
  const [selectedIncidentSeriesKey, setSelectedIncidentSeriesKey] = useState(null);
  const [weekdayFilter, setWeekdayFilter] = useState("all");
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [alertResponses, setAlertResponses] = useState({});
  const [sentResponses, setSentResponses] = useState({});
  const [alertsModalPage, setAlertsModalPage] = useState(1);
  const [isTrackingExpanded, setIsTrackingExpanded] = useState(false);
  const [isReplayModalOpen, setIsReplayModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [isLayoutSettingsOpen, setIsLayoutSettingsOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState(() => ({ ...DEFAULT_VISIBLE_SECTIONS }));

  // Extraire les écoles depuis allAreas (typeAoiId: 24) - MÊME SOURCE QUE LA CARTE
  const schools = useMemo(() => {
    if (!Array.isArray(allAreas)) return [];

    const findSchools = (areas) => {
      let result = [];
      for (const area of areas) {
        if (area?.typeAoiId === 24) {
          result.push(area);
        }
        if (Array.isArray(area?.children)) {
          result = result.concat(findSchools(area.children));
        }
      }
      return result;
    };

    const found = findSchools(allAreas);
    console.log('🏫 Ecoles trouvées depuis allAreas:', found.length);
    return found;
  }, [allAreas]);

  const schoolsLoading = false; // Plus de loading car données déjà chargées





  const selectedIncidentLabel = useMemo(
    () => INCIDENT_SERIES.find((series) => series.key === selectedIncidentSeriesKey)?.label || null,
    [selectedIncidentSeriesKey]
  );

  const filteredIncidentRows = useMemo(() => {
    if (!selectedIncidentLabel) return incidentRows;
    return incidentRows.filter((row) => row.type === selectedIncidentLabel);
  }, [selectedIncidentLabel]);

  // Calcul des dates pour l'API à partir de l'incident sélectionné
  const tripQueryParams = useMemo(() => {
    if (!selectedIncident) return null;

    const vehicleInfo = VEHICLE_MAPPING[selectedIncident.vehicle];
    if (!vehicleInfo) return null;

    // Parser la date de l'incident
    const entryDate = new Date(selectedIncident.entryDate.replace(' ', 'T'));
    const exitDate = new Date(selectedIncident.exitDate.replace(' ', 'T'));

    // Convertir en timestamp Unix
    const fromTimestamp = Math.floor(entryDate.getTime() / 1000).toString();
    const toTimestamp = Math.floor(exitDate.getTime() / 1000).toString();

    return {
      uniqueId: vehicleInfo.uniqueId,
      from: fromTimestamp,
      to: toTimestamp,
      vehicleNumber: vehicleInfo.vehicleNumber,
      incidentDate: selectedIncident.incidentDate,
    };
  }, [selectedIncident]);

  // Hook pour récupérer les données de trajet réelles (avec fallback mock si vide)
  const { tripData: apiTripData } = useTripReplay(
    tripQueryParams ? { ...tripQueryParams, useMockData: true } : { uniqueId: null, from: null, to: null, useMockData: true }
  );

  // Transformer les données API ou mock

  const totalIncidentPages = Math.max(1, Math.ceil(filteredIncidentRows.length / INCIDENTS_PAGE_SIZE));

  useEffect(() => {
    if (incidentPage > totalIncidentPages) {
      setIncidentPage(totalIncidentPages);
    }
  }, [incidentPage, totalIncidentPages]);


  const alertModalPageSize = 10;

  useImperativeHandle(ref, () => ({
    getMapOverview: () => mapOverviewRef.current,
    getDrawShape: () => mapOverviewRef.current?.handleSetDrawShape,
    getDrawAllAois: () => mapOverviewRef.current?.handleSetDrawAllAois,
  }));



  const showClassicEventCenter = true;
  if (showClassicEventCenter) {
    return <EventCenterClassicView />;
  }

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#ebedf0] px-0 py-0">
      <div className="w-full">
        <MonitorConsole
          isTrackingExpanded={isTrackingExpanded}
          onExpand={() => setIsTrackingExpanded(true)}
          onClose={() => setIsTrackingExpanded(false)}
          schools={schools}
          schoolsLoading={schoolsLoading}
        />
      </div>
    </div>
  );
});

export default EventCenter;

